import React, { useRef, useEffect } from 'react';
import { theme } from '../theme';
import { declineInsteadWord, declineInsteadHue, DECLINE_LABEL } from '../services/declineVerdict';
import { showsKopiert, rowClaimVia } from '../services/settledRow';
import { REASON_CODES, isDecisionKind, copyToastMessage, FORM_WORD } from '../constants';
import type { DraftKind, TicketAttributeValues, SenderOrganisationRef, FlagId , TicketDecline } from '../types';
import type { TicketFacets, SenderOrganisationFacet, AttributeFacetValue } from '../services/api';
import { useCategoryMeta, type RowLayout } from '../context/ClassificationSchemaContext';
import { SecondaryAxisPills, pillFace, pillStyle, INFO_PILL_BORDER_WIDTH, type SecondaryPillVariant, type SecondaryAxisLimit } from './SecondaryAxisPills';
import { DecisionBadge } from './DecisionBadge';
import { FristGenanntPill } from './FristGenanntPill';
import { FormPill } from './FormPill';
import { FlagPill, orderedFlags, FLAG_DOMINANCE_ORDER } from './FlagPill';
import { formatRowDate } from '../utils/dateFormatting';
import { InfoTooltip } from './InfoTooltip';
import { usePaletteMode } from '../context/PaletteModeContext';
import { zoneMeta, widestZoneLabel } from '../services/replyEngineMeta';
import { DateRangeInput, DateRangeValue } from './DateRangeInput';
import { TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';
import { TICKET_ATTRIBUTES, type AttributeFilter } from '../services/ticketAttributes';
import { attributeValueStandIn, PRIMARY_SWATCH_BORDER_WIDTH, type PrimarySwatch } from '../services/categoryMeta';
import { useRowPillVisibility, type RowPillVisibility } from '../hooks/useRowPillVisibility';
import { attributeKindId } from '../services/rowPillKinds';
import { visibleFlags, flagImpliedBy } from '../services/impliedSignals';
import { ClerkTrace } from './ClerkTrace';
import { approvedWord, approvedKindWord } from '../services/pressVerdict';
import { SearchIcon, FilterSlidersIcon, PaperclipIcon } from './icons';
// The icon set (picked 2026-10-31) lives in ./icons; re-exported so every caller keeps its import.
export * from './icons';

// ─── BentoCard ───────────────────────────────────────────
// Boundary signal: navy-tinted soft shadow (cardLifted) — static, no hover pop.
// No borders. Shadow gives separation from page bg without bento-box chrome.
// NavyKpiCard keeps its 2pt navy top-rule as the headline-tile signal — different visual register.
export const BentoCard: React.FC<{
  // ReactNode (not just string) so a loading consumer can pass a SkeletonBlock in place of the
  // title text (Task C) without BentoCard needing its own loading branch.
  title: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  headerAction?: React.ReactNode;
}> = ({ title, children, style, contentStyle, headerAction }) => {
  return (
    <div
      data-card
      style={{
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.md,
        boxShadow: theme.shadows.cardLifted,
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        paddingBottom: theme.spacing.md,
        minWidth: 0,
      }}>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 600,
          color: theme.colors.navy, fontFamily: theme.font.family,
          userSelect: 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {headerAction && (
          <div style={{ marginLeft: theme.spacing.sm, flexShrink: 0 }}>{headerAction}</div>
        )}
      </div>
      <div style={{ padding: theme.spacing.md, paddingTop: 0, overflow: 'hidden', ...contentStyle }}>{children}</div>
    </div>
  );
};

export const DownloadIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Why: design dept v2 (~/Documents/xlsx2.svg) — larger text, evenodd letter cutouts, recolored white-on-navy
export const ExcelIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.384767,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796883,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.076167 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 286.95703 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.0918,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 0,102.60743 v 113.3457 c 0,6.93022 5.515604,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.3086,-5.62642 12.3086,-12.55664 V 115.91211 c -1e-5,-6.93023 -5.51563,-12.53494 -12.3086,-12.55664 z m 93.773437,24.85351 c 2.784,0 4.79883,2.01678 4.79883,4.80078 v 52.41602 c 0,4.8 2.016783,8.16015 4.800783,8.16015 h 2.40039 c 2.496,0 4.32031,2.01483 4.32031,4.79883 0,2.784 -2.68929,4.80078 -6.52929,4.80078 h -0.19141 c -8.448003,0 -14.400393,-7.39176 -14.400393,-17.75976 v -52.41602 c 0,-2.784 2.01678,-4.80078 4.80078,-4.80078 z M 138.125,149.54102 c 8.448,0 15.16782,2.87998 19.00782,7.58398 1.728,2.112 1.63123,4.51283 -0.38477,6.04883 -2.112,1.44 -5.27922,0.76736 -7.19922,-1.05664 -3.264,-3.072 -6.71923,-4.03125 -11.61523,-4.03125 -5.664,0 -10.08008,2.01606 -10.08008,5.66406 0,3.552 2.01481,5.66339 12.38281,7.77539 14.4,2.88 19.20117,7.96874 19.20117,15.55274 0,8.736 -7.10462,15.74414 -20.64062,15.74414 -8.928,0 -17.0885,-3.07288 -21.3125,-7.29688 -1.92,-2.016 -1.53613,-4.99075 0.67187,-6.71875 2.496,-2.016 5.18382,-1.53718 7.00782,0.38282 2.208,2.4 7.39204,4.99218 13.24804,4.99218 7.392,0 11.42549,-2.68734 11.52149,-6.52734 0.096,-3.84 -3.07264,-6.62522 -13.05664,-8.44922 -12.96,-2.496 -18.43164,-7.77597 -18.43164,-15.16797 0,-9.792 9.88768,-14.49609 19.67968,-14.49609 z m -101.472653,0.38477 c 1.44,0 2.68843,0.48081 3.64843,1.63281 l 15.74414,18.81445 14.11133,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26562,18.23829 c 2.88,3.36 0.86344,7.39257 -2.97656,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z m 134.400393,0 c 1.44,0 2.68844,0.48081 3.64843,1.63281 l 15.74415,18.81445 14.11132,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26367,18.23829 c 2.88,3.36 0.86539,7.39257 -2.97461,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

// Why: design dept v2 (~/Documents/pdf2.svg) — larger text, evenodd letter cutouts + counter circles for p/d
export const PdfPageIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.695314,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796876,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.386714 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 287.26757 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.40234,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 137.59375,148.29102 c -9.696,0 -17.08789,7.67917 -17.08789,17.95117 0,10.272 7.39189,18.04883 17.08789,18.04883 9.696,0 16.99218,-7.77683 16.99218,-18.04883 0,-10.272 -7.29618,-17.95117 -16.99218,-17.95117 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 72.697264,148.29102 c -9.696,0 -16.99219,7.77487 -16.99219,18.04687 0,10.272 7.29619,17.95313 16.99219,17.95313 9.696,0 17.08789,-7.68113 17.08789,-17.95313 0,-10.272 -7.39189,-18.04687 -17.08789,-18.04687 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 0,102.50391 v 113.3457 c 0,6.93022 5.515596,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.30859,-5.62642 12.30859,-12.55664 V 115.8086 c -1e-5,-6.93023 -5.51562,-12.53494 -12.30859,-12.55664 z m 159.09765,15.06641 c 2.88,0 4.89649,2.01648 4.89649,4.89648 v 43.77539 c -0.096,15.168 -11.42439,26.68945 -26.40039,26.68945 -15.072,0 -26.4961,-11.52145 -26.4961,-26.68945 0,-15.168 10.56017,-26.5918 24.57617,-26.5918 7.584,0 14.11135,3.36033 18.52735,8.73633 V 122.4668 c 0,-2.88 2.01648,-4.89648 4.89648,-4.89648 z m 44.83203,0 c 2.784,0 4.89649,1.92001 4.89649,4.41601 0,2.496 -2.11249,4.41602 -4.89649,4.41602 -7.2,0 -11.90429,5.18494 -11.90429,12.96094 v 2.59179 h 9.79297 c 2.688,0 4.51171,1.72666 4.51171,4.22266 0,2.304 -1.82371,4.0332 -4.51171,4.0332 h -9.79297 v 37.43945 c 0,2.97601 -1.91989,4.89649 -5.08789,4.89649 -2.496,0 -4.70313,-1.92049 -4.70313,-4.89649 v -37.43945 h -4.12891 c -2.688,0 -4.51171,-1.7292 -4.51171,-4.0332 0,-2.496 1.82371,-4.22266 4.51171,-4.22266 h 4.12891 v -1.63281 c 0,-13.728 8.83131,-22.75195 21.69531,-22.75195 z M 72.697264,139.65039 c 15.072,1e-5 26.4961,11.5195 26.4961,26.6875 0,15.168 -10.56018,26.59375 -24.57618,26.59375 -7.584,0 -14.11134,-3.36032 -18.52734,-8.73632 v 25.91992 c 0,2.88 -2.01648,4.89453 -4.89648,4.89453 -2.88,0 -4.89649,-2.01453 -4.89649,-4.89453 v -43.77735 c 0.096,-15.168 11.42439,-26.6875 26.40039,-26.6875 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

export const PinIcon: React.FC<{ size?: number; color?: string; filled?: boolean }> = ({ size = 14, color = 'currentColor', filled }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24"
    fill={filled ? color : 'none'}
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

export const ThumbUpIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

// ─── The handling glyphs (wim vocabulary v7, 2026-09-13) ─────────────────────────────────────
// SIX drawn icons, one per handling the wim vocabulary serves an `icon` name for. They are the
// strip's face in strip_abbr mode: a 32px colour column has room for a glyph or for two letters,
// and a glyph says "forward this" to a reader who has not learnt the Kürzel yet.
// Drawn, never typed: an emoji or a dingbat would be a product-emitted decorative character (house
// rule), platform-dependent in weight and baseline, and untouchable by a stroke width.
// Same contract as every other icon in this file — decorative (svgA11y pins aria-hidden +
// focusable="false"; the strip's meaning lives in its tooltip), 24-unit viewBox, stroke 2, round
// caps and joins, `color` from the caller (the strip passes white onto its own hue).
export const ForwardIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h12" /><path d="M12 6l6 6-6 6" />
  </svg>
);

export const ReplyIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l-6 6 6 6" /><path d="M3 12h10a7 7 0 0 1 7 7" />
  </svg>
);

export const LookupIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" />
  </svg>
);

export const ArchiveIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" /><path d="M5 8v11h14V8" /><path d="M10 12h4" />
  </svg>
);

export const QuestionIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 9a3 3 0 1 1 4.5 2.6c-.9.5-1.5 1.2-1.5 2.4" /><path d="M12 18h.01" />
  </svg>
);

// The sixth glyph (part 5-2): the unclassified bucket's face — a dashed, unfilled circle ("nothing
// assigned"). Same drawn contract as the five above.
export const VoidIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8" strokeDasharray="3 3" />
  </svg>
);

// THE agreed set, and the only mapping from a served `icon` name to a drawn glyph. The vocabulary
// serves a NAME (categoryMeta.resolveIcon deliberately returns no markup), so this table is where a
// name becomes a component — and a name outside it resolves to nothing at all, which is what lets
// HandlingStripFace fall back to the Kürzel instead of drawing a placeholder box for a glyph the
// bundle does not have.
export type HandlingIconName = 'forward' | 'reply' | 'lookup' | 'archive' | 'question' | 'void';

export const HANDLING_ICONS: Record<HandlingIconName, React.FC<{ size?: number; color?: string }>> = {
  forward: ForwardIcon,
  reply: ReplyIcon,
  lookup: LookupIcon,
  archive: ArchiveIcon,
  question: QuestionIcon,
  void: VoidIcon,
};

// The 24px ghost header button both detail hosts inlined for ✕ — single home now.
// `title` splits the HOVER face off the ACCESSIBLE name (fix round 1, 2026-09-09). They are the
// same string for every caller that omits it — and must stay so, that is the ordinary case. The
// split exists for a surface that mounts several of these side by side (PanelSection's per-section
// Maximieren): each needs a name that says WHICH section, while a pointer user can already see
// which control they are on and is better served by the plain verb.
// `disabled` (Part C wave 2, 2026-09-09) keeps a control that is momentarily unavailable IN PLACE
// rather than unmounting it — the house rule for every other disabled affordance in the app: the
// button stays, dimmed, and its `title` says why. Unmounting it instead would move every icon
// beside it, which is the reflow the layout law forbids. The one case that legitimately mounts
// NOTHING is a control that will never apply to that object at all (a receipt has no edit) — an
// absence, not a state.
export const HeaderIconButton: React.FC<{
  label: string; onClick: () => void; children: React.ReactNode; title?: string; disabled?: boolean;
}> = ({ label, onClick, children, title, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={title ?? label}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, padding: 0, cursor: disabled ? 'not-allowed' : 'pointer',
      background: 'transparent', border: 'none', borderRadius: theme.borderRadius.sm,
      color: theme.colors.navyMuted, outline: 'none',
      opacity: disabled ? 0.4 : 1,
    }}
  >
    {children}
  </button>
);

// ─── ActionButton ────────────────────────────────────────
// The bottom-bar button of a ticket detail — the house grammar for a decision the clerk takes on
// the ticket in front of them: FILLED navy = a live action (every clickable button in either bar
// wears it since 2026-09-08, Archivieren included), OUTLINED = the `off` face alone, and a
// disabled button says WHY in its title rather than vanishing.
// Lived in TicketDetail until 2026-09-07, when the triage bottom bar (now PressBar) became the
// second surface built from it — two surfaces, one geometry, so a padding or a border weight can
// never drift between the letter flow's bar and the triage flow's.
// justifyContent centres the label when a caller STRETCHES the button to a pre-allocated slot
// width (the triage bar does); at the natural, content-hugging width it changes nothing.
// `face` (2026-09-08, operator ruling — the triage bar's three-face grammar). An OPT-IN prop: a
// caller that does not pass it gets the filled button, dimmed to 0.6 when disabled. When it IS
// passed, the three faces answer one question — can the clerk get something out of this button?
// The caller-supplied `outlined` escape hatch was DELETED in review round 1 (2026-09-08): the navy
// ruling left it with zero call sites, and the one look it produced is what `off` means now.
//   'on'        — the caller's ordinary filled face, live.
//   'potential' — FILLED and disabled only because the thing it acts on does not exist yet: the
//                 clerk can produce it, and then this same button turns on. Dimmed HARDER than the
//                 ordinary disabled 0.6, because the dimming is the whole message.
//   'off'       — PLAIN white outlined at FULL opacity: navy border, navy text, no wash. Not
//                 clickable, and rendering something will not change that.
// ⚠ `off` deliberately does NOT take the shared disabled 0.6. Dimming is what separates
// `potential` from `on`; a washed-out white would blur the three faces into two-and-a-half, which
// is the exact look this ruling removes. The grammar is: only clickable = full navy,
// could-be-clickable = washed navy, not clickable = full white. `cursor: not-allowed` and the
// German `title` are what say "disabled" on that face, not opacity.
export type ActionButtonFace = 'on' | 'potential' | 'off';

export const ActionButton: React.FC<{
  label: string; onClick: () => void; disabled?: boolean; bg?: string;
  face?: ActionButtonFace; icon?: React.ReactNode; title?: string;
  /**
   * Disabled, but NOT YET visibly so (walk fix round 4, 2026-09-09). A draft action in flight
   * refuses every other draft slot immediately — that half must never be debounced, or a
   * double-submit gets through — while the DIM waits ~250ms, because on a fast round trip the
   * whole bar washed out and back in inside a couple of frames and read as a glitch rather than
   * as a state. This prop is that window and nothing else: the press is already refused.
   * Deliberately narrow: it never applies to a slot disabled for its own reason (a kind this row
   * cannot offer, maintenance) — those are not transients and must look disabled at once.
   */
  undimmed?: boolean;
  /**
   * Which of two LIVE buttons the row recommends (part 5-2, board 3b): `primary` (default) is the
   * filled face; `secondary` is the outline face — white ground, 1.5px navy ring, navy text — for
   * the alternative beside a recommended act. A weight, not a state: availability still comes from
   * `face`/`disabled` exactly as before, and `off` outranks it (an unavailable slot keeps its own
   * outlined face). The ring is an INSET shadow, not a border, so the box is the filled button's
   * box to the pixel — switching weight moves nothing.
   */
  emphasis?: 'primary' | 'secondary';
  /** Fill its container (a PressBar third): full width, the label ending in an ellipsis when the
   *  third is narrower than the verb. */
  fill?: boolean;
}> = ({ label, onClick, disabled, bg, face, icon, title, undimmed, emphasis = 'primary', fill }) => {
  const isOutlined = face === 'off';
  const isQuiet = !isOutlined && emphasis === 'secondary';
  // Precedence, and it matters: 'off' claims full opacity BEFORE the shared disabled fade can
  // apply to it, 'undimmed' then claims it for the pre-dim window of a transient hold, and 'on'
  // otherwise falls through to that fade so an in-flight press still reads as busy.
  const opacity = face === 'potential' ? 0.45
    : face === 'off' ? 1
      : undimmed ? 1
        : disabled ? 0.6 : 1;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
        fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
        color: isOutlined || isQuiet ? theme.colors.navy : theme.colors.white,
        backgroundColor: isOutlined || isQuiet ? theme.colors.white : bg || theme.colors.navy,
        border: isOutlined ? `2px solid ${theme.colors.navy}` : 'none',
        boxShadow: isQuiet ? `inset 0 0 0 1.5px ${theme.colors.navy}` : undefined,
        borderRadius: theme.borderRadius.md,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity,
        // Never wraps: a verb broken onto two lines is a shorter button, and the slots beside it move.
        whiteSpace: 'nowrap',
        transition: 'opacity 0.15s ease, background-color 0.15s ease',
        ...(fill ? { width: '100%', minWidth: 0, height: 40, padding: '0 8px', boxSizing: 'border-box' as const } : {}),
      }}
    >
      {icon}{fill ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{label}</span> : label}
    </button>
  );
};

// ─── ChevronToggleButton ─────────────────────────────────
// Shared expand/collapse chevron chrome (circle + border + soft shadow), extracted from the
// Phase-0 Häufigste-Grund-Codes chevron so the ChatbotAnalyticsCard and the H-G-C card share
// one control. Rotation (180°) is driven by `open`; caller positions it via `style`.
export const ChevronToggleButton: React.FC<{
  open: boolean; onClick: () => void; ariaLabel: string; ariaControls?: string;
  orientation?: 'vertical' | 'horizontal'; style?: React.CSSProperties;
}> = ({ open, onClick, ariaLabel, ariaControls, orientation = 'vertical', style }) => {
  // vertical: down ⌄ (closed) ↔ up ⌃ (open). horizontal: right ▶ (closed) ↔ left ◀ (open),
  // for the sideways-expanding analytics card. Base rotation orients the down-chevron; `open` adds 180°.
  const base = orientation === 'horizontal' ? -90 : 0;
  return (
    <button type="button" onClick={onClick} aria-expanded={open} aria-controls={ariaControls} aria-label={ariaLabel}
      style={{
        width: 26, height: 26, borderRadius: '50%', background: theme.colors.white,
        border: `1px solid ${theme.colors.borderLight}`, boxShadow: '0 2px 6px rgba(0,13,102,0.14)',
        cursor: 'pointer', color: theme.colors.navy, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, ...style,
      }}>
      <span style={{ display: 'inline-flex', transform: `rotate(${base + (open ? 180 : 0)}deg)`, transition: 'transform 0.42s cubic-bezier(0.4,0,0.2,1)' }}>
        <ChevronDownIcon size={14} />
      </span>
    </button>
  );
};

// ─── Spinner ─────────────────────────────────────────────
// Navy ring with one transparent quadrant, rotates via existing stx-spin keyframe.
// Use `label` for inline status; pass `block` for centered full-area placement.
export const Spinner: React.FC<{
  size?: number;
  label?: string;
  block?: boolean;
  color?: string;
}> = ({ size = 28, label, block, color = theme.colors.navy }) => {
  const ring = (
    <span
      aria-label="Lädt"
      style={{
        display: 'inline-block', width: size, height: size,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        animation: 'stx-spin 0.9s linear infinite',
        flexShrink: 0,
      }}
    />
  );
  if (!label && !block) return ring;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      flexDirection: label ? 'column' : 'row',
      ...(block ? { padding: theme.spacing.lg, minHeight: 120 } : {}),
    }}>
      {ring}
      {label && (
        <span style={{
          fontSize: 13, color: theme.colors.navyMid, fontFamily: theme.font.family,
          fontWeight: 500, letterSpacing: 0.2,
        }}>{label}</span>
      )}
    </div>
  );
};

// ─── NavyKpiCard ─────────────────────────────────────────
export const NavyKpiCard: React.FC<{
  // ReactNode (not just string), matching BentoCard's own title prop — a caller reading a served
  // bucket word wraps it in ServedFace, which is not a plain string while /config is still resolving.
  title: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  // Used by StatistikSeite Eingegangen card to render the stats-mode swap toggle.
  headerAction?: React.ReactNode;
  // Gray-out the card surface (e.g. archive-mode treatment on StatistikSeite).
  muted?: boolean;
  // Inline style applied to the title span — used by StatistikSeite to cross-fade titles on mode swap.
  titleStyle?: React.CSSProperties;
  // The third row's child card (Statistik, 2026-10-28): the same surface a size down — title 11 px
  // in the on-dark ink, tighter padding — so a child reads as a part of the card above it.
  compact?: boolean;
}> = ({ title, children, style, headerAction, muted, titleStyle, compact }) => {
  const { mode: palette } = usePaletteMode();
  const arch = theme.colors.mode.archived[palette];
  return (
  <div data-card style={{
    // Muted = archived card surface — light/dark per palette preference, so title flips navy/white.
    // theme.colors.neutral stays defined (sentiment Neutral slice still uses it).
    backgroundColor: muted ? arch.card : theme.colors.navy,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.card,
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden', minWidth: 0,
    // Morph+ recolor — matches DistributionHero mode swap.
    transition: 'background-color 0.58s cubic-bezier(0.4,0,0.2,1)',
    ...style,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', flexShrink: 0,
      padding: compact ? `6px 10px 0` : `${theme.spacing.sm}px ${theme.spacing.md}px`,
      paddingBottom: compact ? 0 : headerAction ? theme.spacing.sm : theme.spacing.md,
    }}>
      <span style={{
        flex: 1, fontSize: compact ? 11 : 14, fontWeight: compact ? 500 : 600, fontFamily: theme.font.family,
        color: muted ? arch.text : compact ? theme.colors.textOnDark : theme.colors.white, userSelect: 'none',
        // Only the archive-lens child title carries an opacity: the Eingegangen card's nodes carry none
        // (kpiFlip pins that it never fades).
        ...(muted && compact ? { opacity: 0.8 } : {}),
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        ...titleStyle,
      }}>
        {title}
      </span>
      {headerAction && (
        <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
          {headerAction}
        </div>
      )}
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, justifyContent: 'center', padding: compact ? `2px 10px 7px` : `${theme.spacing.sm}px ${theme.spacing.md}px` }}>{children}</div>
  </div>
  );
};

// ─── TriStateControl ────────────────────────────────────
export const TriStateControl: React.FC<{
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md';
}> = ({ options, selected, onChange, size = 'sm' }) => {
  const isSm = size === 'sm';
  return (
    <div style={{
      display: 'inline-flex', borderRadius: theme.borderRadius.sm,
      border: `1px solid ${theme.colors.borderLight}`, overflow: 'hidden',
    }}>
      {options.map(opt => {
        const isActive = opt.value === selected;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              border: 'none', cursor: 'pointer',
              padding: isSm ? '2px 8px' : '4px 12px',
              fontSize: isSm ? 11 : 12,
              fontWeight: 500,
              fontFamily: theme.font.family,
              color: isActive ? theme.colors.white : theme.colors.navyMid,
              backgroundColor: isActive ? theme.colors.navy : theme.colors.white,
              transition: 'background-color 0.15s ease, color 0.15s ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// Why: plain-text fields that are NOT email bodies (the attachment digest) must not go through the
// thread-cutting normalizer below — a digest that happens to mention "Von: … Gesendet: …" would be
// truncated by escapeHtmlText's thread-boundary logic. This is the pure escape step only.
// Why the full 5-char set (& < > " '): a near-miss on this branch (task-19/task-21) found a
// caller that builds an HTML attribute (href="...") from this output — `&`/`<`/`>` alone leave
// `"` and `'` free to break out of an attribute and inject markup. A helper whose safety depends
// on the caller already knowing it's attribute-unsafe is a footgun, so the escape set is widened
// here rather than left to each call site to compensate for. Single pass over one map — every
// match is replaced exactly once, so there is no re-escaping hazard to order around.
const escapeMap: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' };

export const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, c => escapeMap[c]);

// Why: plain text (from StripHtmlToPlainText) set via dangerouslySetInnerHTML needs cleanup:
// 1. Strip Outlook safety banner (plain text remnant, can repeat in threaded emails)
// 2. Strip <mailto:>/<https:> URL remnants left by MimeKit in .eml TextBody
// 3. Truncate at first email thread boundary (CS rep reads thread in Outlook)
// 4. Strip mobile/client email footers ("Gesendet mit/von...", "Sent from...")
// 5. Collapse whitespace-only blank lines (Outlook HTML → spaces/nbsp between \n)
// 6. HTML-escape < > & so forwarding headers aren't parsed as HTML elements
// Must run BEFORE styleRedactionTokens so pill <span>s are the only HTML in the output.
export const escapeHtmlText = (s: string): string => {
  // Why: .msg BodyText from Outlook uses \r\n; \r breaks whitespace-collapse regex
  let t = s.replace(/\r\n?/g, '\n');
  // Why: Outlook safety banner includes trailing URL remnant on same line
  t = t.replace(/Sie erhalten nicht häufig E-Mails von[\s\S]*?(?:Erfahren Sie, warum dies wichtig ist|LearnAboutSenderIdentification|SafetyTips)[^\n]*\s*/g, '');
  t = t.replace(/<(?:mailto|https?):.*?>/g, '');
  // Why: only show first message — thread markers: inline forward, Outlook block, web.de format
  // Why: PII redaction replaces dates/names, so markers must match [REDACTED_*] tokens too
  const threadMarkers = [
    /\nAm (?:\d{2}\.\d{2}\.\d{2,4}|\[REDACTED_DATE\]) um \d{2}:\d{2} schrieb/,
    /\n\s*(?:Von:|From:)[\s\S]{0,200}?\n(?:Gesendet|Sent):/,
    /\n(?:Gesendet|Sent):\n[A-Z]/,
    /\n\[REDACTED_[A-Z]+\]\s*<?\[REDACTED_EMAIL\]>?\n(?:Gesendet|Sent):/,
  ];
  let cut = t.length;
  for (const re of threadMarkers) {
    const m = re.exec(t);
    if (m && m.index < cut) cut = m.index;
  }
  t = t.substring(0, cut);
  t = t.replace(/^(?:Gesendet (?:mit |von (?:meinem? |Outlook )).+|Sent from .+)\s*$/gm, '');
  t = t.replace(/\n([ \t\u00a0]*\n){2,}/g, '\n\n');
  t = t.trim();
  return escapeHtml(t);
};

export const isHtml = (text: string): boolean =>
  /<(html|div|p|br|span)\b/i.test(text);

// ─── linkifyUrls ────────────────────────────────────────
// Wraps http(s):// and www. URLs found in already-HTML-escaped text into anchor tags.
// Decodes &amp; back to & only inside the href attribute. Plain rendering uses the matched (escaped) form.
// Run AFTER escapeHtmlText, BEFORE styleRedactionTokens.
const URL_REGEX = /\b(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/g;
export const linkifyUrls = (html: string): string =>
  html.replace(URL_REGEX, (match) => {
    let trimmed = match;
    let trailing = '';
    while (trimmed.length > 0 && /[.,;:!?)]/.test(trimmed.slice(-1))) {
      trailing = trimmed.slice(-1) + trailing;
      trimmed = trimmed.slice(0, -1);
    }
    // Why the trailing .replace(/"/g, '&quot;') is kept though now redundant (task-21): the
    // upstream contract (escapeHtml runs before linkifyUrls, per the module comment above) means
    // no raw " can reach this point anymore — escapeHtml already turned it into &quot;. This is
    // deliberate belt-and-braces for that upstream contract, not dead code: if a future caller
    // ever invokes linkifyUrls on non-pre-escaped text, this still stops attribute breakout on
    // its own. Do not remove without re-verifying the upstream guarantee still holds everywhere.
    const href = (trimmed.startsWith('www.') ? 'https://' + trimmed : trimmed)
      .replace(/&amp;/g, '&').replace(/"/g, '&quot;');
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#000d66;text-decoration:underline">${trimmed}</a>${trailing}`;
  });

// ─── styleRedactionTokens ───────────────────────────────
// Replaces [REDACTED_*] tokens with styled pill spans.
// Must run AFTER DOMPurify.sanitize() — DOMPurify would strip injected spans.

// Maps the BE's [REDACTED_X] type slug → key in personal_data_json.
const TYPE_TO_PD_KEY: Record<string, string> = {
  EMAIL: 'emails', IBAN: 'ibans', PHONE: 'phoneNumbers',
  NAME: 'names', DATE: 'dates', PLZ: 'postalCodes',
};

// Reveal context for click-to-reveal (UploadsSeite pre-process only — see pii-reveal-defensibility.md).
// When `revealCtx` is undefined, behavior is identical to the original redaction-only render.
export interface RevealContext {
  personalData: Record<string, any>;     // keys: emails, ibans, phoneNumbers, names, dates, postalCodes
  revealed: Set<string>;                  // membership key: `${TYPE}:${ordinal}` (0-based)
}

export const styleRedactionTokens = (html: string, revealCtx?: RevealContext): string => {
  // Per-render ordinal counter so the Nth [REDACTED_NAME] maps to personal_data_json.names[N-1]
  const ordinals: Record<string, number> = {};
  return html.replace(
    /\[REDACTED_(EMAIL|IBAN|PHONE|NAME|DATE|PLZ)\]/g,
    (match, type: string) => {
      const idx = (ordinals[type] = (ordinals[type] ?? -1) + 1);
      const escaped = match.replace(/[<>&"']/g, c => escapeMap[c]);

      // Resolve the source value if a reveal context is provided.
      let revealedValue: string | null = null;
      if (revealCtx) {
        const pdKey = TYPE_TO_PD_KEY[type];
        const arr = pdKey ? revealCtx.personalData[pdKey] : undefined;
        if (Array.isArray(arr) && idx < arr.length && typeof arr[idx] === 'string') {
          revealedValue = arr[idx];
        }
      }
      const isRevealed = !!revealedValue && revealCtx?.revealed.has(`${type}:${idx}`);
      const cursor = revealCtx && revealedValue ? 'pointer' : 'default';
      const display = isRevealed
        ? revealedValue!.replace(/[<>&"']/g, c => escapeMap[c])
        : escaped;
      const bg = isRevealed ? '#fff7e6' : theme.colors.bgLight;       // soft amber when revealed → "you're seeing original"
      const border = isRevealed ? theme.colors.accentOrange : theme.colors.borderLight;
      const color = isRevealed ? '#7a3a00' : theme.colors.navyMid;

      const dataAttrs = revealCtx && revealedValue
        ? ` data-pii-type="${type}" data-pii-index="${idx}"`
        : '';
      return `<span${dataAttrs} style="background:${bg};border:1px solid ${border};color:${color};padding:1px 6px;border-radius:3px;font-size:11px;font-weight:500;white-space:nowrap;cursor:${cursor}">${display}</span>`;
    }
  );
};

// ─── PanelSection ───────────────────────────────────────
export const PanelSection: React.FC<{
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  headerAction?: React.ReactNode;
}> = ({ title, children, collapsible, expanded, onToggle, headerAction }) => (
  <div style={{ marginBottom: theme.spacing.lg }}>
    <h3 style={{
      margin: `0 0 ${theme.spacing.sm}px 0`,
      display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
    }}>
      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!!expanded}
            style={{
              background: 'none', border: 'none', padding: 0, margin: 0,
              font: 'inherit', color: 'inherit', cursor: 'pointer',
              userSelect: 'none', display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
            }}
          >
            {title}
            <span style={{ fontSize: 10, color: theme.colors.accentOrange }}>
              {expanded ? '\u25B2' : '\u25BC'}
            </span>
          </button>
        ) : title}
      </span>
      {headerAction && (
        <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: theme.spacing.xs }}>
          {headerAction}
        </div>
      )}
    </h3>
    {(!collapsible || expanded) && children}
  </div>
);

// ─── SkeletonBlock ──────────────────────────────────────
// <purpose>Shared shimmer placeholder for vocab-dependent chrome (Task C loading posture) — a
// consumer reading cm.configLoading renders this in place of any vocab-derived text/tiles
// while /config is still resolving.</purpose>
// <constraints>width/height are CALLER-SUPPLIED to match the real content's footprint exactly
// (UI Layout Stability Rule — nothing may shift when the real content swaps in). NEVER renders
// text: a raw `{category.…}` placeholder string is worse than an empty box, so this component
// has no text prop at all, only dimensions. The shimmer animation itself lives in index.css
// (.stx-skeleton) — prefers-reduced-motion already zeroes it globally, no local escape hatch
// needed here.</constraints>
export const SkeletonBlock: React.FC<{ width?: number | string; height: number | string; borderRadius?: number; style?: React.CSSProperties }> = ({ width = '100%', height, borderRadius = 4, style }) => (
  <div data-testid="skeleton-shimmer" aria-hidden="true" className="stx-skeleton" style={{ width, height, borderRadius, ...style }} />
);

// Default footprint for a ServedFace shimmer — a short German word's worth of pill, the same scale
// DecisionBadges' own per-badge skeleton already uses (RESOLUTION_PILL_*, further down this file).
// Kept as its own pair rather than sharing those constants: this component's callers are free to
// override per their own real content's measured width, and a coincidence of two unrelated numbers
// is not a dependency worth taking.
const SERVED_FACE_SKELETON_WIDTH = 72;
const SERVED_FACE_SKELETON_HEIGHT = 16;

// ─── ServedFace ──────────────────────────────────────────
// <purpose>Renders a served FACE (badge-faces round) honestly, wherever a surface reads
// `useCategoryMeta().face(family, key)` to paint a word on screen — the one place the three-state
// rule (word / served null / not-yet-loaded) meets the DOM, so no consumer hand-rolls its own
// partial version of it (measured, final review: four consumers painted the raw key on a cold load
// and would have crashed on a served null, since `resolveFace`'s old return type lied about ever
// producing one).</purpose>
// <dependencies>useCategoryMeta().configLoading + .face; SkeletonBlock for the loading
// shimmer.</dependencies>
// <constraints>Three states, in order:
//   1. `configLoading` — the vocabulary has not landed yet, so `face` cannot be trusted (it would
//      answer the RAW KEY, same as an absent family). A shimmer, never the key, never a stand-in
//      word (UI Layout Stability: width/height are caller-supplied to match the real content).
//   2. a served `null` — the tenant's own explicit "do not put this on a row" (§13.4). Renders
//      nothing at all by default — not the key, not an empty pill — exactly like every other
//      served-null face this app already honours (resolveAttributeFace's sibling rule). `nullAs`
//      overrides this for the one shape that cannot honour it: a TAB CANNOT BE NAMELESS, so a tab
//      title passes `nullAs="key"` to fall back to the raw key rather than vanish (Task R2).
//   3. a served string (or the absent-family/key raw-key floor, which is a STRING, not null) —
//      `render(word)` draws whatever markup the caller needs around it (icon, testid, title);
//      `render` is never called for state 1, so a caller's markup can assume a real word.</constraints>
export const ServedFace: React.FC<{
  family: string;
  faceKey: string;
  width?: number | string;
  height?: number | string;
  render: (word: string) => React.ReactNode;
  // See constraint 2 above — default 'nothing' preserves every existing caller's behaviour.
  nullAs?: 'nothing' | 'key';
}> = ({ family, faceKey, width = SERVED_FACE_SKELETON_WIDTH, height = SERVED_FACE_SKELETON_HEIGHT, render, nullAs = 'nothing' }) => {
  const cm = useCategoryMeta();
  if (cm.configLoading) {
    return <SkeletonBlock width={width} height={height} borderRadius={10} style={{ flexShrink: 0 }} />;
  }
  const word = cm.face(family, faceKey);
  if (word === null) {
    if (nullAs === 'key') return <>{render(faceKey)}</>;
    return null;
  }
  return <>{render(word)}</>;
};

// ─── CategoryVocabularyMissingNotice ────────────────────
// <purpose>Shared empty-state REPLACEMENT for stats surfaces when cm.vocabularyMissing is true —
// a KNOWN role (config resolved fine, no global RoleConfigWarning) whose tenant has neither a
// classification schema nor vocabulary loaded yet (e.g. freshly-provisioned VNB before taxonomy
// seeding). Without this, categoryKeys=[] renders the surface's generic "Keine Daten" text,
// indistinguishable from a genuinely empty dataset — the silent-empty state the placeholder floor
// forbids. Same shape (minHeight, centered, textSecondary) as each surface's normal empty state so
// swapping this in never shifts layout (UI Layout Stability Rule) — only the text differs.</purpose>
export const CategoryVocabularyMissingNotice: React.FC<{ minHeight?: number }> = ({ minHeight = 120 }) => (
  <div style={{
    minHeight, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: theme.colors.textSecondary, fontSize: 13, textAlign: 'center', padding: '0 12px',
  }}>
    Rollenkonfiguration unvollständig — keine Klassifizierungsdaten verfügbar.
  </div>
);

// The BOX paint of a row's primary-axis colour band (part 7 fix loop, 2026-10-12): the served hue, or
// -- for the unclassified value -- the slate tint (fill #e6e7ec, no visible rim), via the one rule in
// categoryMeta.resolvePrimarySwatch. Every band keys its width explicitly, so border-box keeps the
// border (the tint form: its own fill colour, no visible rim) inside it. No axis yet: the caller's neutral fallback, unbordered.
// `open` (small facts on a row, operator pick C, 2026-10-19): on the OPEN list row the band's border
// is the swatch's INK (`openBorder`), so the strip's box reappears as a thin frame — slate on the
// unclassified value, white on a hue — where the row's grey selection fill would otherwise swallow
// it. Same width, so nothing moves; the border colour alone transitions.
export const primaryBandStyle = (
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'swatch'>,
  value: string | null | undefined,
  fallback: string,
  open = false,
): React.CSSProperties => {
  if (!value) return { backgroundColor: fallback };
  const sw = cm.swatch(value);
  return { backgroundColor: sw.fill, border: open ? sw.openBorder : sw.border, boxSizing: 'border-box', transition: 'border-color 0.15s ease' };
};

// ─── HandlingStripFace + handlingTooltipText ────────────
// <purpose>THE face inside the row's colour strip, and THE text both vocabulary hovers read (wim
// vocabulary v7, 2026-09-13). Three lists render that strip — the active list (AntwortenSeite), the
// ledger (ArchivSeite) and the dashboard's Verlauf (DashboardSeite) — and each used to inline its
// own abbr span. With v7 the face gained a tier order (served glyph → served Kürzel → nothing), and
// a tier order copied three times is three places for it to drift, so it lives here once.</purpose>
// <constraints>The strip's BOX is the call site's — width, flexShrink, background hue, centring all
// stay there, untouched. This component only fills it, which is what keeps the layout law intact:
// no glyph, a Kürzel, or an empty strip are all the same column geometry, so a vocabulary landing
// (or a mode flip) can never re-flow a row. Renders NOTHING for strip_label (line 2's badge speaks
// there) and nothing for a row with no axis yet — an absence, never a placeholder.</constraints>
export const HandlingStripFace: React.FC<{ value: string | null; layout: RowLayout; size?: number }> = ({ value, layout, size = 16 }) => {
  const cm = useCategoryMeta();
  if (!value || layout !== 'strip_abbr') return null;
  const served = cm.icon(value);
  // A served name the bundle has no glyph for is treated exactly like no icon at all: the Kürzel is
  // the honest face, and drawing a stand-in box would assert a meaning nobody served.
  const Glyph = served ? HANDLING_ICONS[served as HandlingIconName] as React.FC<{ size?: number; color?: string }> | undefined : undefined;
  // The ink follows the band: white on a served hue, slate on the tinted unclassified band.
  const ink = cm.swatch(value).ink;
  if (Glyph) return <Glyph size={size} color={ink} />;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, color: ink }}>
      {cm.abbr(value)}
    </span>
  );
};

// The hover text for a classified value, wherever it is shown: the tenant's word, plus the served
// sentence saying what it MEANS when there is one. ONE helper because the strip and the line-2 badge
// name the same value on the same row — two hand-written templates would be two chances to word it
// differently, and the glyph face makes the hint load-bearing rather than decorative.
// A plain colon joins the halves: the product emits no decorative Unicode, so no dash and no arrow.
// A value that serves no hint hovers its label alone — never an empty bubble, never an invented
// sentence (see resolveHint's own single-tier reasoning).
export function handlingTooltipText(
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'label' | 'hint'>,
  value: string,
): string {
  const label = cm.label(value);
  const hint = cm.hint(value);
  return hint ? `${label}: ${hint}` : label;
}

// THE "is this bubble worth a tab stop?" predicate (final review of v7, 2026-09-13) — the companion
// of handlingTooltipText, here for the same reason: the badge surfaces each decide whether to wrap,
// and three copies of the condition would be three places for it to drift.
// A tooltip host is not free. InfoTooltip gives a non-focusable anchor tabIndex=0 so the text is
// keyboard-reachable, which is right when the bubble carries a FACT and wrong when it repeats the
// word already on screen — one dead tab stop per badge, per row. So: wrap when the value serves a
// hint (the sentence saying what it MEANS), or when the visible FACE is not the label (a compact id
// face, where the bubble is the only place the German word appears). Both false ⇒ the badge already
// says everything the bubble would.
// The strip hovers do NOT consult this: a strip renders a glyph or a Kürzel and never the label, so
// its face is never the label and there is always something to say.
export function hasSomethingToSay(
  cm: Pick<ReturnType<typeof useCategoryMeta>, 'label' | 'hint'>,
  value: string,
  face: string,
): boolean {
  return cm.hint(value) !== null || face !== cm.label(value);
}

// ─── CategoryBadge ──────────────────────────────────────
// The badge EXPLAINS ITSELF on hover as of vocabulary v7 (2026-09-13): wim's default row layout
// became strip_label on that date, which makes this badge — not the strip — the face a clerk reads
// all day, and the served `hint` is the sentence saying what the handling means. The bubble is the
// house InfoTooltip carrying handlingTooltipText, the SAME text the strip's own hover shows, so the
// two surfaces on one row cannot word the same fact differently.
// The badge SPAN is untouched (it is the tooltip's anchor, as every InfoTooltip call site passes a
// host element) — nothing about its box, colour or face changed, so no caller's geometry moves.
//
// ONLY WHEN THERE IS SOMETHING TO SAY (final review of v7, same date). InfoTooltip makes its anchor
// keyboard-reachable by giving a non-focusable host tabIndex=0 — correct for a bubble carrying a
// fact, and a tab stop per badge on every list row of gMSB/wMSB/VNB when the bubble only repeats the
// word already printed on the badge. So the wrapper goes on for exactly two reasons: a served hint
// (the sentence saying what the handling MEANS), or a FACE that is not the label (the compact/id
// face, where the bubble is the only place the German word appears). Otherwise the bare span renders
// exactly as it did before v7 — and because InfoTooltip CLONES its host rather than wrapping it in
// an element of its own, the two arms are the same DOM box either way: no metric moves when a
// vocabulary landing adds a hint to a value (UI Layout Stability Rule).
// `tooltip` (Archiv ledger fix, operator ruling B9, 2026-09-16, C3.4: "remove the hover-over from
// the Archivseite") lets a surface rule the bubble OUT entirely, overriding hasSomethingToSay —
// the ledger is a scanning surface, not a deciding one, so its badge never tooltips even for a
// value that would otherwise earn one. Default true, so every call site written before this prop
// existed stays byte-identical.
export const CategoryBadge: React.FC<{ category: string; variant?: 'solid' | 'ghost'; style?: React.CSSProperties; tooltip?: boolean }> = ({ category, variant = 'solid', style, tooltip = true }) => {
  const cm = useCategoryMeta();
  const ghost = variant === 'ghost';
  const face = cm.categoryLabelsFromData ? cm.label(category) : category;
  // part 7 fix loop, 2026-10-12: the unclassified value is the slate tint, not coloured (cm.swatch). The
  // 1.5px border is on every solid badge (its own fill colour) so both forms share one box.
  const sw = cm.swatch(category);
  const badge = (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: ghost ? theme.colors.navy : sw.ink,
      backgroundColor: ghost ? theme.colors.white : sw.fill,
      ...(ghost ? {} : { border: sw.border, boxSizing: 'border-box' as const }),
      borderRadius: theme.borderRadius.sm, padding: '2px 6px',
      display: 'inline-block',
      ...style,
    }}>
      {face}
    </span>
  );
  if (!tooltip || !hasSomethingToSay(cm, category, face)) return badge;
  return <InfoTooltip text={handlingTooltipText(cm, category)}>{badge}</InfoTooltip>;
};

// D3 reply-engine hand-off zone chip (GRÜN/GELB/ROT — exactly three by doctrine; suppression
// is a reason, never a fourth zone). Fail-open: unknown zone strings render raw + neutral.
// InfoTooltip carries the zone legend so the chip is self-explaining without a legend panel.
//
// zone is null/undefined when the tenant's HANDOFF_ZONES lever is off — the envelope omits the
// key rather than shipping null. The honest render is NO chip, but the three call sites place
// this in a flex row where a disappearing chip would shift every sibling (UI Layout Stability
// Rule) — so absent renders the SAME span, same box, just invisible: visibility:hidden +
// aria-hidden, holding the widest doctrine label's width rather than the old neutral '—' text.
//
// Pill house rule shape-borrower (chip register sweep, Part C wave 2, 2026-09-09, see the
// exception note in SecondaryAxisPills.tsx): the METRICS (fontSize/padding/radius/etc, keyed by
// the 'row' variant) come from `pillStyle('info', 'row')`, but the colour stays the hand-off
// zone's own — the doctrine label ("GRÜN"/"GELB"/"ROT") is already the exact face wanted, so it
// does not route through `pillFace`. Both branches below spread the SAME base object so the
// width-reservation contract (the ZoneChip test suite pins this) still holds byte-for-byte.
export const ZoneChip: React.FC<{ zone: string | null | undefined; style?: React.CSSProperties }> = ({ zone, style }) => {
  if (zone === null || zone === undefined) {
    return (
      <span aria-hidden="true" style={{
        ...pillStyle('info', 'row'),
        fontWeight: 700, letterSpacing: 0.4,
        border: '1px solid transparent',
        display: 'inline-block', visibility: 'hidden',
        ...style,
      }}>
        {widestZoneLabel()}
      </span>
    );
  }
  const meta = zoneMeta(zone);
  // Fix wave M4: the legend rides the shared InfoTooltip, not native title= — same span, same
  // box, so the invisible-placeholder branch above still holds the identical width. A zone whose
  // meta carries no legend (the fail-open raw-string render) gets the bare chip: an empty tooltip
  // would add a focus stop that says nothing.
  const chip = (
    <span style={{
      ...pillStyle('info', 'row'),
      fontWeight: 700, letterSpacing: 0.4,
      color: meta.fg, backgroundColor: meta.bg, border: `1px solid ${meta.border}`,
      display: 'inline-block',
      ...style,
    }}>
      {meta.label}
    </span>
  );
  return meta.legend ? <InfoTooltip text={meta.legend}>{chip}</InfoTooltip> : chip;
};

// Neutral GELB review-tier chip — same visual class as ZoneChip's Gelb variant (fg/bg/border
// tokens, pill shape) but for flagging content that needs a human look independent of the D3
// hand-off zone (e.g. a generative-answer segment). theme.colors.warning is the K4-exclusive
// orange and must NEVER be used here — this chip is keyed to theme.colors.zone.gelb instead.
// `hover` (fix wave M4, renamed from `title`): the explanation rides the shared InfoTooltip, not
// a native title= attribute, so the prop no longer names an HTML attribute this chip does not
// emit. Omitted ⇒ the bare chip, with no tooltip and no focus stop.
//
// Pill house rule shape-borrower, same posture as ZoneChip just above — EXCEPT the label here is
// free prose each call site composes, not a fixed doctrine word, so unlike Stufe/Zone this face
// DOES route through `pillFace('info', …)` (chip register sweep, Part C wave 2, 2026-09-09).
export const ReviewChip: React.FC<{ label: string; hover?: string; style?: React.CSSProperties }> = ({ label, hover, style }) => {
  const z = theme.colors.zone.gelb;
  const chip = (
    <span style={{
      ...pillStyle('info', 'row'),
      fontWeight: 700, letterSpacing: 0.4,
      color: z.fg, backgroundColor: z.bg, border: `1px solid ${z.border}`,
      display: 'inline-block',
      ...style,
    }}>
      {pillFace('info', label)}
    </span>
  );
  return hover ? <InfoTooltip text={hover}>{chip}</InfoTooltip> : chip;
};

// Escalation-tier chip (eskalationsstufe 1–3) — the call sites (TicketDetail, DashboardSeite,
// KlaerfallEditor, this file's own TicketRowMeta below, and a since-retired no-draft card) each
// drew their own copy of this span before the chip register sweep (Part C wave 2, 2026-09-09),
// which is exactly the kind of drift the pill house rule exists to prevent (see
// SecondaryAxisPills.tsx).
//
// Part 7, 2026-10-11 (operator ruling): the chip is an INFO pill — outlined, white fill, short,
// icon-free — so it sits in the info tier beside the other outlined pills. It renders for N >= 1
// only: Stufe 0 is the absence of an escalation, never a chip. The metrics come from
// `pillStyle('info', variant)` (a caller on a dense row wants 'row', a caller that is the WHOLE
// header content wants 'detail'); the face does not route through `pillFace` — it is a fixed
// signal, not free prose.
//
// Small facts on a row (operator 2026-10-19, round 3 B, adopted whole): the face is the ROMAN
// NUMERAL of the step (I, II, III) in ORDINARY info-pill ink — the three plum inks the chip wore
// clashed with the sender pill, and plum is the sender pill's alone again. The numeral hovers the
// prompt's own words for the step (delayed, like every hover: a numeral is an abbreviation). The
// kind ships HIDDEN by default (services/rowPillKinds DEFAULT_HIDDEN_ROW_PILLS); a clerk or a
// tenant switches it on under „Hinweise in der Liste".
export const STUFE_FACE: Readonly<Record<1 | 2 | 3, string>> = { 1: 'I', 2: 'II', 3: 'III' };
export const STUFE_HOVER: Readonly<Record<1 | 2 | 3, string>> = {
  1: '1. Erinnerung',
  2: '2. Erinnerung',
  3: 'Letzte Erinnerung, dringend',
};

export const StufeChip: React.FC<{
  n: number;
  variant?: 'row' | 'detail';
  style?: React.CSSProperties;
}> = ({ n, variant = 'row', style }) => {
  if (!(n >= 1)) return null;
  // The face states the same whole step the hover names — a fractional n never prints a half step.
  const step = Math.min(Math.floor(n), 3) as 1 | 2 | 3;
  return (
    <InfoTooltip text={STUFE_HOVER[step]}>
      <span
        data-testid="stufe-chip"
        style={{
          ...pillStyle('info', variant),
          fontWeight: 600, letterSpacing: '0.08em',
          color: theme.colors.navyMid, backgroundColor: theme.colors.white, border: `${INFO_PILL_BORDER_WIDTH} solid ${theme.colors.navyMid}`,
          ...style,
        }}
      >
        {STUFE_FACE[step]}
      </span>
    </InfoTooltip>
  );
};

// The amber "· prüfen" suffix colour — theme.colors.suggestion (harmonized, final review: this used
// to be its own hand-copied literal of the same hex) for the sender-identity registry's one review
// state. Ruling R14, 2026-09-13: the registry's own reviewer signal, not the K4-exclusive orange
// (#BC5A00) or the wim `pruefen` handling hue — a proposed MATCH is a different fact from a
// handling decision, and reusing either token would say the wrong thing on hover.
const SENDER_TOKEN_PRUEFEN_COLOR = theme.colors.suggestion;

// THE gate every registry surface reads (final review, item 2) — one hook, so a surface can never
// disagree about whether this tenant has a sender registry at all. `sender_kind` is the served
// attribute the whole registry is keyed to: the admin tab that reviews the roster is gated on it
// (EinstellungenSeite's availableWhen), the filter section is gated on it
// (SenderOrganisationFilterSection), and the list row's SenderPill (below) is gated on it too — a
// ticket whose organisation carries a `kind` no vocabulary declares has no face to state. A backend
// that matched an organisation while serving no sender_kind is a half-deployed state, not a
// rendering decision, so every registry surface reads the same one condition rather than trusting
// whatever the ticket payload happens to carry. The DETAIL HEADER reads it again (part 7,
// 2026-10-11) for the same SenderPill; its address span stays unconditional — see TicketDetail.
export const useSenderRegistryServed = (): boolean => !!useCategoryMeta().attribute('sender_kind');

// ─── SenderPill ─────────────────────────────────────────
// <purpose>THE SENDER on a list row and in the detail header — ONE pill „Organisation · Art"
// (part 7, 2026-10-11, spec §16.3; supersedes the R33 partner pill AND the separate sender-kind
// attribute pill, which stated the same fact twice and, with the hover, three times). Row 3's
// opening word, in the registry's own token (theme.partnerPill). Where no organisation matched, the
// kind alone; where the kind's served face is null (privat/sonstige stay suppressed), the
// organisation alone; where there is neither, NO pill — never an empty pill, never a raw key.</purpose>
// <dependencies>theme.partnerPill.ink · pillStyle('info','row') for the pill METRICS only ·
// useCategoryMeta().attributeFace('sender_kind', …) for the kind word and .face('registry_status',
// 'proposed') for the hover's review word (both served — never typed here) · InfoTooltip for the
// full name + address.</dependencies>
// <constraints>THE BORDER STATES THE REGISTRY FACT, AND ONLY THAT (R33a/R33b, 2026-09-19, kept):
//   solid border  = a seeded or confirmed organisation — someone stands behind the name;
//   dashed border = a PROPOSAL (auto-created from a domain, unreviewed), a rejected row, or any
//                   status a later backend adds; its hover opens with „Vorgeschlagen – ungeprüft".
// A kind-only pill (no organisation) draws DASHED too: nothing in the registry stands behind it.
// The pill is always OUTLINED — plum ink on white, only the border STYLE moves — and the border
// rides INSIDE the box so solid/dashed is a repaint, never a relayout. A ticket's APPROVAL is stated
// once, by the decision badge beside it. The face is rendered verbatim (an organisation's name and
// a tenant's kind word are not words this product owns, so no propercasing).
// Layout-Stability: the pill takes the slot the kind pill held on the row's meta line and the
// organisation pill held on row 3; it adds no row. It is absent on a row with nothing to state and
// for a clerk who switched „Absender" off, and the only thing to its right on row 3 is a
// marginLeft:auto group, so a pill appearing pushes nothing sideways.</constraints>
export const SenderPill: React.FC<{
  organisation: SenderOrganisationRef | null;
  senderKind: string | null;
  email: string | null;
  /** Mount-site additions (e.g. the detail header's fade-in); merged last, like StufeChip's. */
  style?: React.CSSProperties;
}> = ({ organisation, senderKind, email, style }) => {
  const cm = useCategoryMeta();
  const ink = theme.partnerPill.ink;
  // The kind the REGISTRY row carries wins over the ticket's own stamp: it is the reviewed fact.
  const kind = organisation?.kind ?? senderKind;
  // null = the tenant serves this kind as suppressed (or the ticket states none): say no kind.
  const kindFace = kind ? cm.attributeFace('sender_kind', kind) : null;
  const orgFace = organisation ? (organisation.shortName ?? organisation.name) : null;
  const face = orgFace !== null ? (kindFace !== null ? `${orgFace} · ${kindFace}` : orgFace) : kindFace;
  if (face === null || face === '') return null;
  // „Established" rather than „confirmed": SEEDED rows were never reviewed by anyone and never need
  // to be, so the predicate is the two statuses that mean somebody stands behind the name.
  const established = !!organisation && (organisation.status === 'seeded' || organisation.status === 'confirmed');
  const borderStyle = established ? 'solid' : 'dashed';
  // part 7, 2026-10-11: data-registry is 'proposed' ONLY for a proposal. A rejected row is neither
  // established nor proposed -- it reads 'none' (the fourth state would add nothing a consumer acts
  // on). Core never serves a rejected organisation on a ticket today (SenderIdentityResolver: the
  // kind travels, the organisation does not); this arm covers a row rejected AFTER a ticket was stamped.
  const registry = organisation === null ? 'none' : established ? 'confirmed' : organisation.status === 'proposed' ? 'proposed' : 'none';
  const identity = organisation ? (email ? `${organisation.name} · ${email}` : organisation.name) : email;
  // The review word is the SERVED registry_status.proposed face; a null face (served as "say
  // nothing") drops the word and keeps the rest of the sentence.
  const proposedWord = cm.face('registry_status', 'proposed');
  // Only a PROPOSAL carries the served „Vorgeschlagen" word; any other unestablished status (a
  // rejected row) is merely „ungeprüft" -- it must never read as proposed.
  const unreviewed = organisation?.status === 'proposed' && proposedWord !== null ? `${proposedWord} – ungeprüft` : 'ungeprüft';
  const hover = organisation && !established ? [unreviewed, identity].join(' · ') : identity;
  const pill = (
    <span
      data-testid="sender-pill"
      // The state as DATA as well as paint: 'confirmed' (seeded reads as confirmed), 'proposed'
      // (every other status), 'none' (kind alone, no organisation).
      data-registry={registry}
      style={{
        ...pillStyle('info', 'row'),
        color: ink,
        backgroundColor: theme.colors.white,
        border: `${INFO_PILL_BORDER_WIDTH} ${borderStyle} ${ink}`,
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        ...style,
      }}
    >
      {face}
    </span>
  );
  return hover ? <InfoTooltip text={hover}>{pill}</InfoTooltip> : pill;
};

// ─── TicketAttributePills ───────────────────────────────
// <purpose>THE rendering of a ticket's classified-alongside ATTRIBUTES (wim vocabulary v7,
// 2026-09-13) — one info pill per bound attribute whose served face is a word. All three surfaces
// that state them mount THIS: the list row's line 2 (TicketRowMeta below), the detail header's meta
// line (TicketDetail) (the triage panel's chip row is gone, part 6). One component, because the
// interesting behaviour here is a SUPPRESSION — the tenant declares that the majority commodity and
// a private sender are not worth a pill — and a surface carrying its own copy of that rule is how
// two screens end up disagreeing about the same mail.</purpose>
// <dependencies>useCategoryMeta().attributeFace — the ONLY face source (its null means "render no
// pill"; see its resolver for the three states of the served faces map) · TICKET_ATTRIBUTES
// (services/ticketAttributes) for which attributes exist as ticket fields, and in which ORDER ·
// pillStyle('info', …) for the register.</dependencies>
// <constraints>SLOT ORDER IS FIXED and comes from the binding table, never from the data: a value
// arriving or resolving to nothing may not re-arrange the pills beside it (UI Layout Stability).
// The face is rendered VERBATIM — this is a documented exception to the info register's face rule
// (pillFace), and the only one whose reason is the tenant rather than the product: a served face is
// a spelling the tenant chose, and propercasing it would rewrite a word whose first letter is
// deliberately lowercase. The register's SHAPE still comes from the factory.
// No pill while cm.configLoading: with no vocabulary the resolver answers the propercased value by
// design ("nothing was declared"), so painting during the first /config fetch would show a face the
// settled row then removes — and because the common values are exactly the suppressed ones, that is
// the majority of rows, not an edge. A skeleton would reserve width for the same mostly-nothing, so
// the honest pre-settle state is empty (the standing loading posture's one exception: shimmer where
// something WILL land, nothing where it usually will not).</constraints>
export const TicketAttributePills: React.FC<{
  ticket: TicketAttributeValues;
  variant: SecondaryPillVariant;
  /**
   * The clerk's „Hinweise in der Liste" set (P2 Task R4, 2026-09-18) — passed by the LIST ROW and
   * by nobody else. It is a prop rather than a hook read inside this component precisely because
   * the preference is scoped to list rows: the detail header and the triage panel mount the same
   * pills and must keep stating every attribute, whatever a clerk hid from their list. Absent =
   * every attribute renders, which is what every pre-preference call site means.
   */
  visibility?: RowPillVisibility;
}> = ({ ticket, variant, visibility }) => {
  const cm = useCategoryMeta();
  if (cm.configLoading) return null;
  return (
    <>
      {TICKET_ATTRIBUTES.map(binding => {
        // A hidden attribute renders NOTHING — no pill, no slot. The toggle lives in Einstellungen,
        // so there is no on-screen transition to protect against (Layout-Stability), and a reserved
        // width would be permanent empty air on every row.
        if (visibility && !visibility.isVisible(attributeKindId(binding.name))) return null;
        // part 7, 2026-10-11: the sender kind is stated by the one SenderPill (row 3 / detail meta
        // line), never as a pill of its own — the binding stays for the filter and its facets.
        if (binding.name === 'sender_kind') return null;
        const value = ticket[binding.field];
        // Absent and null both mean "this row says nothing about it" — see TicketAttributeValues.
        if (!value) return null;
        const face = cm.attributeFace(binding.name, value);
        // The SERVED SUPPRESSION. Never `?? attributeValueStandIn(value)` here: that collapses the
        // tenant's decision into the "no decision" case and puts a pill on every row.
        if (face === null) return null;
        return (
          <span
            key={binding.name}
            data-testid={`attribute-pill-${binding.field}`}
            style={pillStyle('info', variant)}
          >
            {face}
          </span>
        );
      })}
    </>
  );
};

// ─── TicketRowMeta ──────────────────────────────────────
// The tenant's inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE), applied to the AGGREGATE
// the backend measured rather than to individual attachment rows — every list-row paperclip site
// (TicketRowMeta, UploadsSeite, DashboardSeite) has only hasAttachments + documentAttachmentCount to
// work from, never the per-attachment rows the detail pane's countAttachments (attachmentOcrStatus.ts)
// consumes. One function for all three sites so the rule can't drift between them the way the three
// paperclip glyphs already had. `?? (hasAttachments ? 1 : 0)` covers both a bundle running against a
// backend that doesn't serve the count yet AND a row stamped before the count existed — both read as
// "keep today's glyph", never as an invented absence. The deliberate, documented consequence: a
// pre-stamp row's documentAttachmentCount (when the field DOES exist on an old row) equals its total
// attachment count, inline images included — so on a flag-OFF tenant that legacy row keeps its
// paperclip even though its detail pane already says "Keine Dokumente beigefügt". Fail-open by
// design; decays as those tickets archive. Do not "fix" this into a disappearing paperclip.
export const shouldShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
  attachmentsCountInline: boolean,
): boolean => attachmentsCountInline
  ? !!ticket.hasAttachments
  : (ticket.documentAttachmentCount ?? (ticket.hasAttachments ? 1 : 0)) > 0;

// The SUPERSET of shouldShowAttachmentGlyph: true for every row that could still end up with a
// paperclip under EITHER posture, false for rows no counting rule can ever give one. It exists to
// scope the reservation below — a row with no attachments at all must reserve NOTHING, because
// reserving width it will never use makes the settle drop that width plus its flex gap, which is
// the very content shift the slot was added to prevent, on the majority of rows.
export const mayShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
): boolean => !!ticket.hasAttachments || (ticket.documentAttachmentCount ?? 0) > 0;

// Reserves the paperclip's footprint during the pre-settle config-loading window (cm.configLoading)
// so nothing shifts when shouldShowAttachmentGlyph's verdict becomes trustworthy — painting a glyph
// and correcting it on settle is a content shift (Layout-Stability). Render it ONLY behind
// mayShowAttachmentGlyph. `size` is caller-supplied and must equal the PaperclipIcon size that site
// actually paints (13 in TicketRowMeta, 12 in the two page-level list rows) — a slot wider than its
// glyph shifts on settle in exactly the direction it was meant to prevent.
export const AttachmentGlyphSlot: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <span data-testid="attachment-glyph-slot" style={{ width: size, flexShrink: 0 }} aria-hidden="true" />
);

// THE DECISION BADGE — the head of ROW 3's right-hand Bearbeitung group (ROW COMPOSITION v1, part 7
// fix loop, 2026-10-12), and the whole of what the row says about the clerk's decision
// (operator rulings R31/R32, 2026-09-18, amended by the row-grammar ruling of the same date, and
// again by the row-grammar ruling of 2026-09-24, R34, and by the press of 2026-10-28): the badge
// says the clerk APPROVED. On a tenant that states more than one Bearbeitung
// (`cm.statesApprovedKind`) it names the approved kind — „Weiterleitung genehmigt", the served kind
// word in fixed German — in that kind's handling hue. On every other tenant (gMSB/wMSB/VNB today)
// it reads „Genehmigt" in the row's classification hue: the clerk approved the classified answer.
// Only FORMAL approvals count (approvedRowKinds / the detail's approve claims): a copy is the
// „Kopiert" trace and never a „genehmigt". The DETAIL dates the decision; a list row never does.
//
// Three faces retired into this one (all of them words this app chose rather than the tenant):
//   „Genehmigt"/„Kopiert" — the R20 status split, which existed only because the pill repeated the
//     category badge's word whenever the approved kind matched the classified one. Stating the SAME
//     served word twice (R34) makes that repetition the honest reading rather than a fault to
//     rename around, so the split — and its „Genehmigt" fallback for a silent vocabulary — has
//     nothing left to solve. No fallback word exists for this badge any more, on any tenant.
//   „<Wert> (Kopie)" — the indirect-approval suffix. Row 3's „Kopiert" badge already states that,
//     and a decision reads the same whichever gesture made it.
//   „empfangsbestätigt" — the ack override (CLAIM_KIND_PILL_FACE, deleted). Under R31 the receipt
//     is NOT a decision: it carries no badge here at all, and its signal is row 3's
//     „Eingang bestätigt".
//
// The face resolves through ONE seam, exactly as the hue does: the served `decisionKindHandling`
// (`useCategoryMeta().decisionKindHandling`, part 4c — was the code constant `CLAIM_KIND_HANDLING`)
// maps the claimed kind to a handling id, and cm.label/cm.color answer for it — so shared.tsx still
// spells no handling id, and re-wording a handling's face stays a seed edit everywhere it appears.
// Two kinds sharing a handling therefore share a face, which is the honest reading: the badge
// states what the decision resolved to, not which button made it. The HOVER names the approved
// label always, and the classified one too whenever it differs — „Vom Sachbearbeiter entschieden:
// Antworten (klassifiziert: Weiterleiten)" — so a clerk hovering an equality badge sees no bracket
// at all, and a clerk hovering a deviation learns both words without leaving the row.
//
// The guard AND the type proof for a claim kind off the wire (string[], not DraftKind[]). The
// served map's own keys are the only kinds this surface recognises — an ABSENT map (a generation-9
// backend, or a test double that passes none) recognises NOTHING, which is the honest reading: no
// guessed badge (Review Focus 3). A plain `kind in map` narrows nothing for a Record-typed target
// (no index signature) — a declared type predicate is what actually narrows `string` to `DraftKind`
// here.
function isResolvablePillKind(kind: string, map: Readonly<Record<string, string>>): kind is DraftKind {
  return Object.prototype.hasOwnProperty.call(map, kind);
}

/** THE decision kinds on a row, in served order — the one resolution DecisionBadges renders from.
 *  An unknown kind (or every kind, on an absent `decisionKindHandling`) contributes nothing rather
 *  than crashing or showing a raw id, and `ack` contributes nothing by RULING (isDecisionKind): a
 *  receipt is not a decision. */
const decisionKindsOf = (claims: string[] | null | undefined, kindHandling: Readonly<Record<string, string>>): DraftKind[] =>
  (claims ?? []).filter((kind): kind is DraftKind => isResolvablePillKind(kind, kindHandling)).filter(isDecisionKind);

// (`hasDecisionBadge`, the exported twin of decisionKindsOf, is DELETED with ruling R33a: the
// sender pill was its one reader, and the pill now states the REGISTRY fact rather than the
// ticket's approval. The decision badge is the only approval signal on the row again, so the predicate
// has exactly one reader and does not need to be one.)

// The loading shimmer's box. HEIGHT is the badge's own painted height: a 10px line box plus 1px of
// padding top and bottom — the 1px border rides inside it via boxSizing. 16 is that figure rounded
// up, and it is safe to round up in one direction only: both pill rows below are `minHeight: 18`
// with `alignItems: 'center'`, so any box under 18 costs the row no height at all — which is what
// makes the swap vertically free, not the exactness of this number.
// WIDTH is a plain estimate again, and allowed to be: the badge heads row 3's right-aligned
// Bearbeitung group (since 2026-10-12), so the shimmer settling to the word's real width grows the
// group leftward into free space and pushes nothing sideways. That is the same fact that retired the MEASURED slot before this ruling
// — a reservation with nothing to protect is dead weight on every row in the fleet.
const RESOLUTION_PILL_HEIGHT = 18;
const RESOLUTION_PILL_SKELETON_WIDTH = 72;

/** THE DECISION — the head of row 3's Bearbeitung group, mounted identically by the list row and the detail (and by
 *  the Archiv ledger's compact line). One component rather than two renders of the same rule: a
 *  clerk who opens a row must not find the detail saying something different from the row they
 *  clicked, and a surface that re-implements the face logic is exactly how that drift starts — the
 *  same reason TicketAttributePills exists. The DATE rides here for that same reason: a date beside
 *  a badge is one statement, and two surfaces composing it from their own halves is how one of them
 *  ends up dating a decision the other does not.
 *
 *  ⚠ ONLY THE DETAIL DATES A DECISION since ruling R33 (2026-09-19): the dates left the list rows,
 *  so every row mount passes no `decidedAt` at all and a multi-kind row's badge stands alone there.
 *  The prop stays because the detail's decision line still carries the stamp — and it stays HERE,
 *  rather than in the detail, for the reason above.
 *
 *  Returns a FRAGMENT, not a wrapper: both call sites are flex rows whose gap and hierarchy belong
 *  to them, and a div here would put a box around a group their own layout already owns.
 *
 *  `claims` is the row's `draftClaims`, untyped past the network boundary (string[], not
 *  DraftKind[]) — see isResolvablePillKind. `decidedAt` is the stamp each surface resolves from
 *  the shape it holds (services/draftClaims.decisionClaimAt, the detail's keyed claims — a list row
 *  passes none at all); absent/null states the decision badge UNDATED rather than inventing one.
 *  `primaryAxis` is the row's classified
 *  handling, read only to word the hover's „(klassifiziert: …)" clause on a deviation.
 *
 *  NOTHING when there is no decision on a MULTI-kind tenant — no empty reservation, by ruling
 *  (2026-09-18): the badge sits inside row 3's right-aligned Bearbeitung group, so a
 *  decision landing shifts nothing and there is nothing to reserve against. Row 3 keeps its height
 *  from its own minHeight. A SINGLE-kind tenant's approval reads „Genehmigt" (see the component doc
 *  above).
 *
 *  ⚠ WHICH of those two arms applies is itself unknown while `cm.configLoading` is true (the served
 *  `decisionKindHandling` floors to `{}` until /config settles, so `cm.statesApprovedKind` cannot be
 *  trusted yet) — the function therefore asks THAT question first, off `claims` alone, before
 *  either arm gets to ask its own (see the loading gate below). */

// The DECISION's own date — extracted (final review, minor) from the two arms below that each drew
// an identical span: the single-kind arm (the DETAIL's decision date, alone, no badge beside it —
// that IS the single-kind grammar) and the multi-kind arm's date-before-badge pairing (which DOES always have
// a badge beside it, since it only renders inside the `badges.length > 0` branch). One component,
// one footprint, so the two can never drift into two sizes for the same fact.
const DecisionDateSpan: React.FC<{ decidedAt: string; variant?: SecondaryPillVariant }> = ({ decidedAt, variant }) => (
  <span
    data-testid="decision-date"
    style={{
      fontSize: variant === 'detail' ? 13 : 11,
      color: variant === 'detail' ? theme.colors.textSecondary : theme.colors.navyMuted,
      flexShrink: 0,
    }}
  >
    {formatRowDate(decidedAt)}
  </span>
);

/** The kinds a LIST ROW's clerk formally approved: its claims minus the ones made by copying (a kind
 *  missing from the via map reads as approve, the legacy posture — see TicketListItem). */
export const approvedRowKinds = (t: { draftClaims?: readonly string[] | null; draftClaimVia?: Record<string, string> | null }): string[] =>
  (t.draftClaims ?? []).filter(kind => t.draftClaimVia?.[kind] !== 'copy');

export const DecisionBadges: React.FC<{
  /** The kinds the clerk APPROVED (formal approvals only — a copy is the „Kopiert" trace, never a
   *  „genehmigt"; see approvedRowKinds / approvedClaimKinds). */
  claims?: string[] | null;
  decidedAt?: string | null;
  variant?: SecondaryPillVariant;
  /** The row's classification — the hue a one-kind tenant's „Genehmigt" wears (the clerk approved
   *  the classified answer). */
  primaryAxis?: string | null;
}> = ({ claims, decidedAt, variant = 'row', primaryAxis }) => {
  const cm = useCategoryMeta();
  // Served order, through the ONE resolution — decisionKindsOf below. Read UP HERE, ahead of both
  // arms: `cm.decisionKindHandling` (unlike `cm.categoryKeys`/`cm.label`, which the loading gate
  // below still guards) is safe to read before the gate, exactly like the map `cm.statesApprovedKind`
  // is derived from — it floats in from the warm session cache ahead of the fetch settling, and
  // floats to `{}` on a genuinely cold load, which resolves no kind and is therefore already the
  // honest "nothing yet" shimmer count.
  const kinds = decisionKindsOf(claims, cm.decisionKindHandling);

  // THE LOADING GATE, checked BEFORE either arm (fix, 2026-09-24 review round 1): on a COLD load
  // `decisionKindHandling` is always `{}` until /config resolves (the context's own floor), so
  // `cm.statesApprovedKind` cannot be trusted yet — reading it here would flash the single-kind
  // arm's date on a tenant that does state a Bearbeitung, then swap it for the badge once the
  // real value lands a moment later. A content swap on first paint is exactly what the shimmer
  // posture exists to prevent (UI Layout Stability), so the loading state is decided BEFORE either
  // arm asks its own question, off the one fact that IS already known — whether this row carries a
  // real decision at all (`kinds`, pure over `claims`). A decided row gets the same badge-sized
  // shimmer the multi-kind arm would paint per badge; an undecided row reserves nothing, exactly as
  // both settled arms already do for that case.
  if (cm.configLoading) {
    if (kinds.length === 0) return null;
    return (
      <>
        {kinds.map((kind, i) => (
          <SkeletonBlock
            key={`${kind}-${i}`}
            width={RESOLUTION_PILL_SKELETON_WIDTH}
            height={RESOLUTION_PILL_HEIGHT}
            borderRadius={4}
            style={{ flexShrink: 0 }}
          />
        ))}
      </>
    );
  }

  if (!cm.statesApprovedKind) {
    // A tenant whose served `decisionKindHandling` states at most one Bearbeitung (an absent map
    // reads as `{}`): the approval cannot differ from the classification, so the badge says only
    // THAT the clerk approved — „Genehmigt", in the classification's own hue (the press,
    // 2026-10-28; replaces the 2026-10-19 rule that earned a one-kind tenant no badge for the yes).
    // Any approved decision kind counts here, the filing (`archive`) included: core claims the
    // filing when a letter-flow row is approved without a letter. The DETAIL dates it.
    const approvedAny = (claims ?? []).some(kind => isDecisionKind(kind as DraftKind));
    if (!approvedAny) return null;
    return (
      <>
        {decidedAt && <DecisionDateSpan decidedAt={decidedAt} variant={variant} />}
        <DecisionBadge
          act="approved"
          hue={(primaryAxis && cm.color(primaryAxis)) || theme.colors.navy}
          word={approvedWord(cm.face)}
          variant={variant === 'detail' ? 'detail' : 'row'}
          testId="done-badge"
        />
      </>
    );
  }

  // `kinds` (decisionKindsOf(claims, cm.decisionKindHandling)) was already resolved above, ahead of
  // the loading gate — this branch only reaches here once `cm.configLoading` is settled false, so
  // every entry below has a real vocabulary to read from (the shimmer this branch used to paint
  // per-badge now lives entirely in the loading gate above; folded there rather than kept here too,
  // since past this point `cm.configLoading` can only ever be false and a second check would be dead
  // code).
  const badges = kinds.map((kind, i) => {
    const handling = cm.decisionKindHandling[kind];
    // Defensive, not reachable in practice: `kind` already passed isResolvablePillKind against this
    // same map above, so `handling` is always found here — kept rather than a non-null assertion so
    // a future change to that filter fails safe (no badge) instead of crashing a row.
    if (!handling) return null;
    // The APPROVED handling's present-tense label — the same word the classified badge would wear
    // for this handling, R34. No fallback: every tenant that reaches this branch serves its labels
    // (cm.label floors to the legacy resolver, never to a word this app invents).
    // The press (2026-10-28): the badge says the clerk APPROVED this kind — „Weiterleitung
    // genehmigt" — the served kind word in fixed German, as the bar's „… genehmigen" does.
    const approved = approvedKindWord(kind, cm.face);
    // The DECISION BADGE (small facts on a row, round 4 A.4, operator 2026-10-19): the primary
    // axis's own squared filled shape in the approved handling's served hue, the check as its
    // tinted key — one component with the declined-instead badge (DecisionBadge). It replaced the
    // outlined companion pill of 2026-09-09 and its hover: the word IS the long form, and the
    // classified word a deviation differs from stands one line up on row 2 (house rule: hover only
    // when it reduces load).
    return (
      <DecisionBadge
        key={`${kind}-${i}`}
        act="approved"
        hue={cm.color(handling)}
        word={approved}
        variant={variant === 'detail' ? 'detail' : 'row'}
        testId="done-badge"
      />
    );
  });
  if (badges.length === 0) return null;
  return (
    <>
      {/* The DATE the decision was made, reading directly before the word for what was decided —
          „18.09.26 Weiterleiten". Only where a stamp exists — this arm only reaches here with at
          least one badge already in `badges` (the `badges.length === 0` guard above), so THIS arm's
          date never renders without one; the single-kind arm above is a separate grammar and does
          render its date alone (see its own comment) — the two must not be conflated. The DETAIL is
          the only surface that passes a stamp now (R33) — a list row states the badge undated. */}
      {decidedAt && <DecisionDateSpan decidedAt={decidedAt} variant={variant} />}
      {badges}
    </>
  );
};

// <purpose>Rows 2+3 of a ticket-list row, shared across Antworten/Archiv so the layout can't
// drift. ROW COMPOSITION v1 (part 7 fix loop, 2026-10-12, operator rulings from the composition
// board) — the house rule, verbatim in smarttix-receiver.md:
//   Row 2 left  — the primary axis (in-strip label or badge, the tenant's UI_ROW_LAYOUT switch) ·
//     the pending chip (no category yet) · the secondary-axis pills · the paperclip · the
//     Neu-Analyse count (a note on the classification).
//   Row 3 left  — the TENANT-EXCLUSIVE plum pill FIRST when present (SenderPill on a
//     sender-registry tenant; FormPill „Formular" on a form-extraction tenant when the mail carries
//     a recognised form; nothing yet elsewhere) · then the info pills: flags · Frist genannt · Stufe n ·
//     the utility exception pill („Gas").
//   Row 3 right — the Bearbeitung group: the approved-as badge (DecisionBadges) · Kopiert ·
//     Bearbeitet · Eingang bestätigt.
// THE DATES ARE GONE from both lines (R33): the arrival is the row-1 age the PAGES mount beside the
// unread dot (ArrivalAge — part 7 fix loop, 2026-10-12: the one clock), the decision date lives on
// the detail's decision line.</purpose>
// <constraints>Row 3 renders on EVERY row at row 2's own minHeight (18) — pre-allocated, so a row
// with no plum pill, no info pill and no Bearbeitung stands as tall as a full one (Layout-Stability).
// The Bearbeitung group uses marginLeft:auto — no fixed widths; a badge or trace landing grows it
// leftward into free space and pushes nothing. The left groups are `display: contents` wrappers
// (testids + order guards, no box), so an empty group occupies nothing and leaves no artefact.
// maxSecondaryAxisVisible is caller-supplied (the per-page pill cap differs).
// strip_abbr (Kürzel im Streifen) is rendered by the caller inside the colour strip, not here —
// in BOTH branches since 2026-09-09 (see categoryBadge for which word the badge wears).
// compact (Archiv ledger): ONE meta line carrying the SAME three groups in the same order; the
// ledger's own visible date is the ARCHIVE stamp, on the caller's subject line.
// Pending chip: the BE bucket boundary lets 'categorizing'/'categorized'/'generating_suggestion'
// tickets (category NULL, no suggestion yet) into the same buckets that already carry 'reanalyzing'
// — TRANSIENT_STATUSES (useTicketDetailRefresh) is reused rather than a second status list. Shown
// ONLY when there's no category yet, so an in-progress reanalysis (which keeps its prior category)
// renders exactly as before.</constraints>
export const TicketRowMeta: React.FC<{
  // The attribute pair rides in through the shared shape (types.ts) rather than as two more
  // hand-listed optional fields: this prop is a structural double of a list row, and the ONE
  // declaration is what keeps the double from drifting from the DTO it stands in for.
  ticket: TicketAttributeValues & {
    primaryAxis?: string;
    // Drives the pending chip only — absent/unrecognized status is simply never pending
    // (fail-open, same posture as every other optional field here).
    status?: string;
    secondaryAxis?: string[];
    hasAttachments?: boolean;
    // Documents only, per the ingest stamp — see shouldShowAttachmentGlyph above for how this and
    // hasAttachments combine under the tenant's counting rule.
    documentAttachmentCount?: number;
    reanalysisCount?: number;
    // The two DECISION stamps a list payload carries. NEITHER is read as a date here any more
    // (R33: no row states one) — `copyApprovedAt` is read on its own by the right group's
    // „Kopiert" badge, which states the GESTURE, and `approvedAt` is carried for the callers that
    // age a row by it. Whether a row is DECIDED is read off the claims (decisionKindsOf, via
    // DecisionBadges), never off these: an ack-only row carries a stamp and is not decided.
    approvedAt?: string | null;
    copyApprovedAt?: string | null;
    edited?: boolean;
    eskalationsstufe?: number | null;
    // Flag ids raised on the row (TicketListItem.flags). Additive: absent on a pre-flag row, which
    // renders no pill.
    flags?: string[];
    // The letter check's first deadline quote (TicketListItem.deadlineQuote): „Frist genannt".
    deadlineQuote?: string | null;
    // Which draft kinds this row already carries a claim for — resolution pills, in served order
    // (additive — absent on pre-claim-family backends, rendered in BOTH branches once core serves it
    // to Archiv rows too). string[], not DraftKind[] — same posture as `flags` above: the
    // wire is untyped past the network boundary, and the unknown-kind guard below is only a real
    // guard if the type it narrows admits a value the served decisionKindHandling doesn't recognise.
    draftClaims?: string[] | null;
    // Presence of a recognised form (TicketListItem.hasFormExtraction) — the row half of the
    // „Formular" pill's gate; the tenant half is the served capability (cm.formExtractionEnabled).
    hasFormExtraction?: boolean;
    // The standing „Nicht hilfreich" verdict (TicketListItem.decline, core mig 090) — an Archiv-row
    // fact (a declined row is always filed), rendered as the Bearbeitung group's verdict pill:
    // „Stattdessen: <word>" when the clerk said what they did instead, „Nicht hilfreich" otherwise.
    decline?: TicketDecline | null;
    // Sender-identity registry (P2, 2026-09-13) — additive, absent on every pre-registry row and on
    // a sender with no matched organisation. Renders as SenderPill, leading row 3 (R33).
    senderOrganisation?: SenderOrganisationRef | null;
    customerEmail?: string | null;
    // NOTE `draftClaimVia` (kind -> "approve" | "copy") is served but DELIBERATELY not read here
    // since R32 (2026-09-18): the decision badge states the approved handling's own label,
    // and a decision reads the same whichever gesture made it. Row 3's „Kopiert" badge is where
    // the copy gesture is stated — once, off `copyApprovedAt`, never re-derived from the claim map.
  };
  maxSecondaryAxisVisible: SecondaryAxisLimit;
  compact?: boolean;
  // Tenant-wide inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE). Defaults true so every
  // existing test double / call site written before this prop existed keeps today's exact glyph
  // behaviour (hasAttachments alone) rather than silently flipping posture.
  attachmentsCountInline?: boolean;
}> = ({ ticket: t, maxSecondaryAxisVisible, compact = false, attachmentsCountInline = true }) => {
  const cm = useCategoryMeta();
  // Read HERE as well as inside the pill: row 3 must decide whether there is a LEFT GROUP at all,
  // and a wrapper rendered around a component that answers null would be an empty flex item — the
  // reservation this row grammar exists to refuse.
  const senderRegistryServed = useSenderRegistryServed();
  // „Hinweise in der Liste" (P2 Task R4, 2026-09-18). Consulted per SIGNAL below; a hidden signal
  // renders NOTHING — no slot, no spacer. Outside App's provider (every pre-R4 test double) every
  // signal is visible, so this read is behaviour-neutral until a clerk or an admin says otherwise.
  // Which signals may be hidden at all is the registry's call, never this file's:
  // services/rowPillKinds, held to this row by components/__tests__/rowPillRegistryParity.test.tsx.
  const rowPills = useRowPillVisibility();
  // Everything on these rows that is not a classification speaks the INFO register (pill house
  // rule, operator ruling 2026-09-08, walk round 4): pending / reanalysis / kopiert / bearbeitet
  // all spread this, and their faces go through pillFace so the
  // Propercase voice is the register's decision and not a spelling each call site remembers.
  const pill: React.CSSProperties = pillStyle('info', 'row');
  // part 7 round 2, 2026-10-13: every served secondary code is drawn, on the normal row and the
  // compact Archiv line alike — a code the band makes redundant is a vocabulary defect, not a pill to hide.
  const secondaryAxis = t.secondaryAxis ?? [];
  const eskN = t.eskalationsstufe && t.eskalationsstufe >= 1
    ? (Math.min(Math.max(t.eskalationsstufe, 1), 3) as 1 | 2 | 3) : null;
  // Category as a badge — in BOTH branches only when the operator is on strip_label; strip_abbr
  // renders the Kürzel in the caller's own colour strip instead (Antworten always had one, and the
  // Archiv ledger grew one on 2026-09-09 — see below).
  // ⚠ compact used to be exempt from that rule ("the Archiv has no strip, so it always badges").
  // The exemption is retired by operator ruling (walk fix round 2, 2026-09-09): it put the served
  // handling word on the row twice — once as this badge and once as the resolution pill beside it,
  // which names the same handling — in every mode, because the mode could not reach this row.
  // Which WORD the badge wears is a second question, and it is decided by the role's own DEFAULT:
  //   default strip_label (vnb, and wim since vocabulary v7) — the served label. Long names are
  //     precisely why that is the role's default, and its rows are the ones the label was written for.
  // ⚠ wim CHANGED BRANCH on 2026-09-13 (vocabulary v7, operator ruling: strip_label is its default
  // now), so a wim ledger row at its default wears the served word here rather than the axis id.
  // That re-opens, latently, the doubling the 2026-09-09 ruling closed — a row carrying a resolution
  // pill would name the same handling twice — but core does not serve draftClaims on ARCHIVED rows
  // yet, so no ledger row shows both today. Left as the rule states it rather than re-keyed to a
  // second proxy: which face a wim ledger badge should wear now is an operator call on the
  // badge/pill register, and this rule still says exactly what it always said.
  //   default strip_abbr (gMSB/wMSB) — the axis ID. Such a role is only on strip_label because
  //     a clerk deliberately flipped it, and the short id is what the strip would have carried:
  //     the row states the axis without repeating the pill's served word.
  // The id is the honest short form here rather than cm.abbr: the Kürzel belongs to the STRIP's
  // idiom (it is sized for a 32px colour column), and a badge is not that column.
  // ⚠ SCOPED TO compact — the ruling named the Archiv row, and the active list was not part of it.
  // The same doubling is visible there (a flipped wim tenant's active row badges „Weiterleiten"
  // beside a resolution pill saying „Weiterleiten"), and this rule would close it identically; it
  // is deliberately NOT applied there without a ruling, because the active list is a surface the
  // walk did not cover. Recorded here rather than silently generalised.
  // `?? ''` on BOTH arms (never just the label one): the face is compared against the label by
  // hasSomethingToSay below, which wants a definite string — and the no-axis row this guards for
  // never renders a badge at all (the `t.primaryAxis &&` gate below).
  const badgeFace: string = compact && cm.rowLayoutDefault === 'strip_abbr'
    ? (t.primaryAxis ?? '')
    : cm.label(t.primaryAxis ?? '');
  // The hover (vocabulary v7, 2026-09-13) is on the OWN-MARKUP branch only: the CategoryBadge branch
  // below carries the identical bubble inside the component itself, and wrapping it here too would
  // nest two tooltips on one span. Same helper both ways, so the text cannot diverge — and it names
  // the VALUE, which matters most on the compact face that shows the short id: the badge stays short
  // and the hover is where the served word and sentence live.
  // …and only when there IS something to say (final review of v7, same date): the same
  // hasSomethingToSay predicate CategoryBadge uses, so a badge whose face is already the label and
  // whose value serves no hint stays a plain span rather than a tab stop carrying its own echo.
  const ownMarkupBadge = (axis: string) => (
    <span data-testid="category-badge" style={{ ...pill, fontWeight: 600, color: cm.swatch(axis).ink, backgroundColor: cm.swatch(axis).fill, border: cm.swatch(axis).border, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', lineHeight: '14px' }}>
      {badgeFace}
    </span>
  );
  // compact (Archiv ledger fix, operator ruling B9, 2026-09-16, C3.4: "remove the hover-over from
  // the Archivseite") drops the badge's hover altogether, on BOTH branches, whatever
  // hasSomethingToSay would otherwise say — the ledger is a scanning surface, not a deciding one.
  // `compact` is TicketRowMeta's own Archiv signature (only ArchivSeite passes it), so it is also
  // the flag this suppression keys on; every other row (the active list, uncompact) is untouched.
  const categoryBadge = cm.rowLayout === 'strip_label' && t.primaryAxis && (cm.categoryLabelsFromData ? (
    !compact && hasSomethingToSay(cm, t.primaryAxis, badgeFace)
      ? <InfoTooltip text={handlingTooltipText(cm, t.primaryAxis)}>{ownMarkupBadge(t.primaryAxis)}</InfoTooltip>
      : ownMarkupBadge(t.primaryAxis)
  ) : (
    <CategoryBadge category={t.primaryAxis} style={{ fontSize: 10, padding: '1px 6px', flexShrink: 0 }} tooltip={!compact} />
  ));
  // Pending chip — see <constraints>. Only when there's genuinely no category yet; a ticket that
  // already has one (e.g. mid-reanalysis) keeps its existing categoryBadge treatment untouched.
  const pendingChip = !t.primaryAxis && !!t.status && TRANSIENT_STATUSES.has(t.status) && (
    <span data-testid="pending-chip" style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Spinner size={10} />{pillFace('info', 'Wird verarbeitet…')}
    </span>
  );
  // The zone (GRÜN/GELB/ROT) chip lives on detail surfaces only (KI-Vorschlag, PopoutShell slim
  // header — via the ZoneChip component below) — one visual home for the zone signal, not
  // duplicated onto every list row. The model's one-line status is retired fleet-wide (part 6,
  // 2026-10-07): pills are facets, never model-written summaries.
  // The FLAG pill (part 5-2; part 7, 2026-10-11: one served word, no glyph, no age, on EVERY tab
  // and in the compact Archiv ledger row): a recorded signal, the first INFO pill on row 3 after
  // the tenant-exclusive plum pill (ROW COMPOSITION v1, 2026-10-12). It carries no hover: a list row
  // serves flag ids only (no quote), and an info pill hovers a served explanation or nothing (part 7
  // round 3, 2026-10-14). Nothing is reserved on an unflagged row. Never hideable by a
  // preference: a flag is a safety signal. Hidden ONLY when the visible band implies it (the
  // implication rule, part 7 fix loop, 2026-10-12 — e.g. a served implied landing: the band IS the flag).
  const flagPills = orderedFlags(visibleFlags(cm, t.flags ?? [], t.primaryAxis)).map(id => (
    <FlagPill key={id} id={id} />
  ));
  // THE secondary axis pill, ported to SecondaryAxisPills — see its own header for the ruling
  // (de-slugged-lowercase face, standard colouring, full proper-case hover, never abbreviated, no
  // role exceptions). This row used to run a three-tier fallback here that let a vocabulary
  // delivery jump the face straight from the de-slugged id to the full German label (the divergence
  // formerly logged as D-6) — that transitional behaviour ends here; the component always renders
  // the de-slugged face regardless of what the tenant's data source is.
  // TicketRowMeta, DashboardSeite, TicketDetail, and StatistikSeite now share this one
  // component. UploadsSeite still calls formatSecondaryAxisCode/getReasonCodeLabel directly for its own
  // INTERACTIVE (removable) reason-code chip — not this migration's scope, same boundary as
  // FilterPanel's filter bubble below (own markup/active-state, resolution accessors only).
  // ChatbotTestSeite's two reason-code chips are plain (non-interactive) display and simply were
  // not in this task's surface list — a real residual, not a deliberate boundary like the two above.
  const secondaryAxisPills = rowPills.isVisible('process_type')
    && <SecondaryAxisPills codes={secondaryAxis} maxVisible={maxSecondaryAxisVisible} variant="row" />;
  // The classified-alongside attributes (vocabulary v7, 2026-09-13) — see TicketAttributePills for
  // the suppression rule and the fixed slot order. Rendered in BOTH branches: unlike
  // the flag pill (an in-play signal the settled ledger row drops), the commodity and the
  // sender kind are permanent facts about the mail, and a ledger row that stopped stating them
  // would read as a different mail from the one the active list showed.
  const attributePills = <TicketAttributePills ticket={t} variant="row" visibility={rowPills} />;
  // Reserve only for rows that could get a glyph (mayShowAttachmentGlyph) — a row with no
  // attachments has nothing to reserve for, and giving it a slot would make settle collapse the
  // width AND its flex gap on the majority of rows.
  const attachment = mayShowAttachmentGlyph(t) && (
    cm.configLoading ? <AttachmentGlyphSlot size={13} /> : shouldShowAttachmentGlyph(t, attachmentsCountInline) && (
      <span data-testid="attachment-glyph" title="Anhang" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
        <PaperclipIcon size={13} color={theme.colors.navyMuted} />
      </span>
    )
  );
  // ── ROW COMPOSITION v1 (part 7 fix loop, 2026-10-12, operator rulings from the composition
  // board) — three GROUPS, the same three on the list row and on the Archiv compact line:
  //   CLASSIFICATION (row 2 left): primary band label/badge · pending chip · secondary pills ·
  //     paperclip · the Neu-Analyse count (a note ON the classification, so it sits with it).
  //   INFO (row 3 left): the TENANT-EXCLUSIVE plum pill FIRST (sender pill on a sender-registry
  //     tenant; „Formular" on a form-extraction tenant; nothing yet elsewhere) · flags · Frist genannt ·
  //     Stufe n · the attribute pills (the utility exception, „Gas").
  //   BEARBEITUNG (row 3 right, marginLeft:auto): approved-as badge · Kopiert · Bearbeitet ·
  //     Eingang bestätigt.
  // The CLASSIFICATION group is a `display: contents` wrapper: it names the group (testids, order
  // guards) without adding a flex box, so each pill stays a direct flex item of its line.
  // The INFO group is a real box that GIVES WAY (part 7 fix loop, 2026-10-12, closing review): on a
  // narrow pane its tail clips (`flex: 0 1 auto; minWidth: 0; overflow: hidden`) while the
  // Bearbeitung group (`flexShrink: 0`, `marginLeft: auto`) stays whole — the decision is never cut
  // mid-word, and the info group's FIRST pills (the plum pill, the flags) are the ones that stay
  // visible. An empty info group is a zero-width box; the Bearbeitung group's auto margin absorbs
  // its gap, so nothing moves.
  const reanalysisTrace = rowPills.isVisible('reanalysis') && t.reanalysisCount != null && t.reanalysisCount > 0 && (
    <ClerkTrace kind="reanalysis" count={t.reanalysisCount} />
  );
  const classificationGroup = (
    <span data-testid="row-group-classification" style={{ display: 'contents' }}>
      {categoryBadge}
      {pendingChip}
      {secondaryAxisPills}
      {attachment}
      {reanalysisTrace}
    </span>
  );

  // THE sender pill, one gate for both branches: the clerk's „Absender" switch, the registry gate,
  // and something to state.
  const senderPill = rowPills.isVisible('sender_organisation') && senderRegistryServed && (t.senderOrganisation || t.senderKind) && (
    <SenderPill organisation={t.senderOrganisation ?? null} senderKind={t.senderKind ?? null} email={t.customerEmail ?? null} />
  );
  // THE „Formular" pill (part 7 fix loop, 2026-10-12): the clerk's „Formular" switch, the row's own
  // recognised form, and the SERVED form-extraction capability — the same gate as the Excel-batch
  // button (App's /config `formExtractionEnabled`, carried by the schema context). Never a role id.
  // While the capability is still unknown (warm load, /config not yet answered) a form-bearing row
  // RESERVES the pill's exact footprint (FormPill `reserved`, visibility: hidden), so the pill lands
  // in place and the info pills after it never move; if the capability lands off, the reservation
  // collapses (AttachmentGlyphSlot's pattern).
  const formPill = rowPills.isVisible('formular') && t.hasFormExtraction === true && (
    cm.capabilitiesLoading ? <FormPill reserved /> : cm.formExtractionEnabled && <FormPill />
  );
  const infoGroup = (
    <span data-testid="row-group-info" style={INFO_GROUP_STYLE}>
      {senderPill}
      {formPill}
      {flagPills}
      {rowPills.isVisible('frist_genannt') && <FristGenanntPill quote={t.deadlineQuote} />}
      {rowPills.isVisible('eskalationsstufe') && eskN && (
        <StufeChip n={eskN} variant="row" />
      )}
      {attributePills}
    </span>
  );

  // Row 3 right (small facts on a row, operator ruling 2026-10-19): the Bearbeitung traces as split
  // pills, then THE DECISION LAST — the approved-as badge or the declined-instead badge, one
  // component (DecisionBadge) in the chosen word's served hue.
  const bearbeitungGroup = (
    <span data-testid="row-group-bearbeitung" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {rowPills.isVisible('kopiert') && showsKopiert({ ...t, claimVia: rowClaimVia(t) }) && <ClerkTrace kind="kopiert" />}
      {rowPills.isVisible('bearbeitet') && t.edited === true && <ClerkTrace kind="bearbeitet" />}
      {/* The RECEIPT's own signal (R31/R32, 2026-09-18). It reads `draftClaims` — the claim family —
          rather than a stamp of its own, because a receipt IS a claim. Deliberately NOT
          `copyApprovedAt`: core stopped stamping that for an ack copy, since a companion artifact is
          not a copy-approval. The WORD is the served `decision.receipt` face (ServedFace's
          three-state honesty); the tooltip is the copy toast's own sentence
          (`copyToastMessage('ack', face)`, constants.ts), read from where it is composed. */}
      {rowPills.isVisible('eingang_bestaetigt') && (t.draftClaims ?? []).includes('ack') && (
        <ClerkTrace kind="eingang_bestaetigt" title={copyToastMessage('ack', cm.face)} />
      )}
      {/* The yes badge (DecisionBadges: „Genehmigt", or „<Art> genehmigt" where kinds are stated).
          UNDATED on every row: the decision date lives on the detail's decision line. */}
      <DecisionBadges claims={approvedRowKinds(t)} primaryAxis={t.primaryAxis} />
      {/* The verdict (bottom-bar decision, 2026-10-16; the badge since 2026-10-19): what the clerk
          did INSTEAD of the suggestion, the same word and hue the detail's decision line shows
          (declineInsteadWord / declineInsteadHue — one home). A decline without a word: the key
          segment alone. */}
      {t.decline && (
        <DecisionBadge
          act="instead"
          hue={declineInsteadHue(cm, t.decline)}
          word={declineInsteadWord(cm, t.decline)}
          label={DECLINE_LABEL}
          testId="decline-pill"
        />
      )}
    </span>
  );

  // Compact (Archiv ledger): ONE line carries the three groups in the same order — the row-2/row-3
  // split cannot be drawn there; the date lives on the caller's subject line.
  if (compact) {
    return (
      <div data-testid="row-line-compact" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {/* The decision never clips (B5 walk, 2026-10-27: the Archiv row cut a decline badge to
            „St"). The classification and the info pills share ONE lane that may shrink and fades at
            its edge; the Bearbeitung group after it keeps its full width. */}
        <span data-testid="row-line-compact-lane" style={{
          display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 auto', minWidth: 0, overflow: 'hidden',
          maskImage: 'linear-gradient(to right, black calc(100% - 14px), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 14px), transparent)',
        }}>
          {classificationGroup}
          {infoGroup}
        </span>
        {bearbeitungGroup}
      </div>
    );
  }

  return (
    <>
      {/* Row 2 — what the mail IS: the classification and its notes. */}
      <div data-testid="row-line-2" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {classificationGroup}
      </div>
      {/* Row 3 — the info pills left, the Bearbeitung right. PRE-ALLOCATED: it renders on every row
          at row 2's own 18px minHeight (every pill here is at most 18px tall: the split pills and the
          decision badge are exactly 18, the outlined pills less), so a row with no plum
          pill, no info pill and no Bearbeitung stands exactly as tall as a full one and nothing
          below it moves when a group fills (UI Layout Stability). */}
      <div data-testid="row-line-3" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {infoGroup}
        {bearbeitungGroup}
      </div>
    </>
  );
};

// Row 3's / the compact line's INFO group box — see TicketRowMeta's ROW COMPOSITION note. Shrinks
// (minWidth 0) and clips its own tail; never wraps (the line is one row high).
const INFO_GROUP_STYLE: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
};

// ─── FilterPanel ─────────────────────────────────────────
// <purpose>Reusable search + filter dropdown. Controlled — parent owns all state.</purpose>
// <dependencies>theme, REASON_CODES from constants; useCategoryMeta (role-aware category set)</dependencies>
// <constraints>Click-outside closes dropdown. Live filtering (no apply button). Relative positioning.</constraints>

interface FilterPanelProps {
  search: string;
  onSearchChange: (v: string) => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;

  // Optional sections — rendered only when callback provided
  timeFilter?: string | null;
  onTimeFilterChange?: (v: string | null) => void;
  categoryFilter?: string | null;
  onCategoryFilterChange?: (v: string | null) => void;
  reasonCodeFilter?: string[];
  onReasonCodeFilterChange?: (codes: string[]) => void;
  // Classified-alongside ATTRIBUTE sections (wim vocabulary v7, 2026-09-13). Keyed by SERVED
  // attribute name (snake_case, exactly as the vocabulary spells it) — the same key the section
  // heading and every bubble resolve through, so nothing re-keys a selection on its way to the
  // page's own state. One callback for ALL sections rather than one per attribute: how many
  // sections exist is the tenant's decision, and a per-attribute prop would put that decision in
  // this interface.
  attributeFilter?: AttributeFilter;
  onAttributeFilterChange?: (name: string, values: string[]) => void;
  // part 7, 2026-10-11 (spec 16.5): every pill kind is a facet, worded as the pill is. The
  // „Warnhinweis" section (the flag pills; counts from facets.flags) follows the optional-section
  // convention: rendered only when the callback is wired AND the tenant serves the words.
  flagFilter?: FlagId[];
  onFlagFilterChange?: (ids: FlagId[]) => void;
  // The „Formular" facet (part 7 fix loop, 2026-10-12): the form pill's own word as ONE bubble,
  // rendered only when the callback is wired AND the tenant serves form extraction
  // (cm.formExtractionEnabled — never a role id). Its count is the visible bucket's served
  // form-bearing subset, already resolved by the page (formFilterCountOf below).
  hasFormFilter?: boolean;
  onHasFormFilterChange?: (on: boolean) => void;
  formFilterCount?: FormFilterCount;
  // Sender-identity registry (P2, 2026-09-13), Task R2 — the „Absender" organisation section.
  // Same optional-section convention as every other block here: rendered only when the caller
  // wires onSenderFilterChange AND the vocabulary declares sender_kind (see the section itself).
  // `facets` backs BOTH this section (its org row set + counts) and the count span every generic
  // attribute bubble above now carries — one prop rather than two, since a page that fetches one
  // fetches the other in the same response.
  senderFilter?: string[];
  onSenderFilterChange?: (ids: string[]) => void;
  facets?: TicketFacets | null;
  // Set by useTicketFacets once a facets read has FAILED (a backend with no /tickets/facets, most
  // likely). Every count span then renders nothing at all rather than a „–" that will never resolve
  // — one reflow, at the first failure, instead of a permanent placeholder (final review, item 4).
  facetsUnsupported?: boolean;
  uploaders?: string[];
  filterUser?: string;
  onFilterUserChange?: (v: string) => void;
  filterDateRange?: string;
  onFilterDateRangeChange?: (v: string) => void;
  dateRangeLabel?: string;

  // Server-side ISO date-range picker (Task 5 / ArchivSeite). Additive: when
  // onDateRangeChange is provided the DateRangeInput renders INSTEAD of the
  // free-text filterDateRange field.
  from?: string | null;
  to?: string | null;
  onDateRangeChange?: (range: DateRangeValue) => void;

  onClearAll: () => void;
  hasActiveFilters: boolean;
}

const TIME_OPTIONS = [
  { label: '7 Tage', value: '7d' },
  { label: '30 Tage', value: '30d' },
  { label: 'Dieser Monat', value: 'thisMonth' },
] as const;

// `swatch` (part 7 fix loop, 2026-10-12): the UNCLASSIFIED category's bubble wears its primary swatch
// (categoryMeta.resolvePrimarySwatch -- the slate tint; slate fill + white ink when active) even when inactive, so it
// reads as a failed classification, not another category. Padding gives back what the heavier border
// takes (PRIMARY_SWATCH_BORDER_WIDTH over the plain 1px), so every bubble keeps one box.
const swatchBorderExtra = parseFloat(PRIMARY_SWATCH_BORDER_WIDTH) - 1;
const bubbleStyle = (active: boolean, swatch: PrimarySwatch | null = null): React.CSSProperties => ({
  padding: swatch ? `${3 - swatchBorderExtra}px ${10 - swatchBorderExtra}px` : '3px 10px', fontSize: 11, fontWeight: active || swatch ? 600 : 400,
  color: active ? (swatch ? swatch.activeInk : theme.colors.white) : swatch ? swatch.ink : theme.colors.navyMuted,
  backgroundColor: active ? (swatch ? swatch.activeFill : theme.colors.navy) : swatch ? swatch.fill : 'transparent',
  border: swatch ? (active ? `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${swatch.activeFill}` : swatch.border) : `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
  borderRadius: 20, cursor: 'pointer', fontFamily: theme.font.family,
  transition: 'all 0.15s ease', outline: 'none', userSelect: 'none' as const,
});

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11, color: theme.colors.textSecondary, display: 'block',
  marginBottom: 6, fontFamily: theme.font.family,
};

const filterInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box' as const,
  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
  fontSize: 13, fontFamily: theme.font.family, outline: 'none',
  backgroundColor: theme.colors.white,
};

// The count span every countable filter bubble carries (attribute bubbles AND the organisation
// section below) — `–` while the facet answer for this bubble hasn't loaded yet, the served number
// once it has. ALWAYS rendered (never conditionally omitted): the alternative — no span until a
// count exists — is the layout shift the bubble register exists to prevent (UI Layout Stability).
// `unsupported` (final review, item 4) is the ONE case that renders no span at all: the backend
// answered no facets and never will this session, so „–" would state "still loading" forever.
const FacetCount: React.FC<{ count: number | undefined; unsupported?: boolean }> = ({ count, unsupported }) => {
  if (unsupported) return null;
  return <span style={{ opacity: 0.7, marginLeft: 4 }}>{count === undefined ? '–' : count}</span>;
};

// The „Formular" bubble's count, as a page resolves it from its list counts (part 7 fix loop,
// 2026-10-12): a NUMBER once the bucket's form-bearing subset is served; 'pending' while the first
// list answer is still out (the bubble pre-allocates the „–" every count span shows while loading);
// 'unserved' when the answer came without one (an older core) — then the bubble carries NO count
// span, never a fabricated 0.
export type FormFilterCount = number | 'pending' | 'unserved';
export function formFilterCountOf<B extends string>(
  formBearing: Partial<Record<B, number>> | undefined,
  bucket: B,
  loading: boolean,
): FormFilterCount {
  const n = formBearing?.[bucket];
  if (typeof n === 'number') return n;
  return loading ? 'pending' : 'unserved';
}

// The one place a bubble's FACE text sits in its own element rather than as the button's bare text
// node — required so a FacetCount sibling never changes what `getByText(face)` matches (the
// button's OWN textContent would otherwise become "Face<count>", no longer equal to the face
// alone). Shared by the attribute bubbles below and SenderOrganisationFilterSection's own bubbles.
const FacetLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => <span>{children}</span>;

// The organisation bubble's own style: the interactive chip register (bubbleStyle) with the
// registry's review-state colour (SENDER_TOKEN_PRUEFEN_COLOR, ruling R14 — the sender-identity
// registry's own amber, distinct from the K4-exclusive orange and the wim `pruefen` handling hue)
// standing in for the border on a PROPOSED match, dashed so
// it reads as "needs a decision" rather than "selected". Deliberately NOT gated on `active`: a
// clerk may select a proposed org to filter by it, and the review cue must survive that click.
const senderOrgBubbleStyle = (active: boolean, proposed: boolean): React.CSSProperties => ({
  ...bubbleStyle(active),
  ...(proposed ? { border: `1px dashed ${SENDER_TOKEN_PRUEFEN_COLOR}` } : {}),
});

// The kind heading's own style (part 7, 2026-10-11): a heading that is also a toggle. Smaller and
// quieter than an organisation bubble so the two registers stay apart, with the SAME border width
// in both states (transparent when off) so pressing it never moves a pixel.
const senderKindHeadingStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center',
  padding: '1px 8px', marginBottom: 3, fontSize: 10, fontWeight: 600,
  color: active ? theme.colors.white : theme.colors.textSecondary,
  backgroundColor: active ? theme.colors.navy : 'transparent',
  border: `1px solid ${active ? theme.colors.navy : 'transparent'}`,
  borderRadius: 20, cursor: 'pointer', fontFamily: theme.font.family,
  transition: 'all 0.15s ease', outline: 'none', userSelect: 'none' as const,
});

// ─── SenderOrganisationFilterSection ────────────────────
// <purpose>THE „Absender" filter — one section for the one sender pill („Organisation · Art", part 7,
// 2026-10-11). Its KIND HEADINGS are the sender_kind facet (clickable: they set
// `attributeFilter.sender_kind`, the existing `senderKind` param — sender_kind has no section of its
// own any more), and under each heading sit that kind's matched organisations (facets
// .senderOrganisations), each face `shortName ?? name` exactly as the pill wears it.</purpose>
// <dependencies>useCategoryMeta().attribute('sender_kind') for the kind ORDER (its declared
// `values`) and .attributeFace for each kind's word; TicketFacets.senderOrganisations for the org
// row set + counts and TicketFacets.attributes.sender_kind for the heading counts, both computed
// server-side with their own dimension released.</dependencies>
// <constraints>Every DECLARED kind gets a heading — the filter register: a kind whose served face is
// `null` (no word on the pill) is still filterable, headed by the propercased stand-in, the same
// fallback every filter bubble uses (categoryMeta.attributeValueStandIn). A PROPOSED organisation
// sits under its own kind with the dashed review border (per-row, status === 'proposed'); part 7
// deleted the trailing review group that used to collect them. An organisation of a kind the
// vocabulary never declares is listed after the headed groups WITHOUT a heading — never under an
// invented one, never dropped. Headings are toggles only when the page wires the attribute filter;
// organisations are listed only when it wires the organisation filter.</constraints>
const SenderOrganisationFilterSection: React.FC<{
  organisations: SenderOrganisationFacet[];
  kindOrder: string[];
  faceFor: (kind: string) => string;
  kindCounts: AttributeFacetValue[] | undefined;
  facetsLoaded: boolean;
  selectedKinds: string[];
  onKindsChange?: (kinds: string[]) => void;
  selected: string[];
  onChange?: (ids: string[]) => void;
  facetsUnsupported?: boolean;
}> = ({ organisations, kindOrder, faceFor, kindCounts, facetsLoaded, selectedKinds, onKindsChange, selected, onChange, facetsUnsupported }) => {
  const byKind = new Map<string, SenderOrganisationFacet[]>();
  const unheaded: SenderOrganisationFacet[] = [];
  if (onChange) {
    for (const org of organisations) {
      if (!kindOrder.includes(org.kind)) { unheaded.push(org); continue; }
      const list = byKind.get(org.kind) ?? [];
      list.push(org);
      byKind.set(org.kind, list);
    }
  }

  const toggle = (id: string) =>
    onChange?.(selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id]);
  const toggleKind = (kind: string) =>
    onKindsChange?.(selectedKinds.includes(kind) ? selectedKinds.filter(v => v !== kind) : [...selectedKinds, kind]);

  // A kind's organisations form a group NAMED by the kind's face; the unheaded remainder (kinds no
  // vocabulary declares) has no name to give, so it is a plain row inside the „Absender" group
  // rather than an unnamed group (final review, part 7, 2026-10-11).
  const orgRow = (orgs: SenderOrganisationFacet[], name?: string) => (
    <div role={name ? 'group' : undefined} aria-label={name} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {orgs.map(org => {
        const active = selected.includes(org.id);
        const proposed = org.status === 'proposed';
        return (
          <button key={org.id} type="button" style={senderOrgBubbleStyle(active, proposed)}
            title={org.name} onClick={() => toggle(org.id)}>
            <FacetLabel>{org.shortName ?? org.name}</FacetLabel>
            <FacetCount count={org.count} unsupported={facetsUnsupported} />
          </button>
        );
      })}
    </div>
  );

  // a11y: the section is itself a `group` labelled „Absender", so each kind's organisation group is
  // nested under a parent that names what the kinds divide.
  return (
    <div role="group" aria-label="Absender" style={{ marginBottom: theme.spacing.sm }}>
      <span style={filterLabelStyle}>Absender</span>
      {kindOrder.map(kind => {
        const face = faceFor(kind);
        const orgs = byKind.get(kind) ?? [];
        const active = selectedKinds.includes(kind);
        const count = facetsLoaded ? (kindCounts?.find(v => v.value === kind)?.count ?? 0) : undefined;
        return (
          <div key={kind} style={{ marginBottom: 4 }}>
            {onKindsChange ? (
              <button type="button" aria-pressed={active} style={senderKindHeadingStyle(active)}
                title={face} onClick={() => toggleKind(kind)}>
                <FacetLabel>{face}</FacetLabel>
                <FacetCount count={count} unsupported={facetsUnsupported} />
              </button>
            ) : (
              <span style={{ ...filterLabelStyle, fontSize: 10, marginBottom: 3 }}>{face}</span>
            )}
            {orgs.length > 0 && orgRow(orgs, face)}
          </div>
        );
      })}
      {unheaded.length > 0 && orgRow(unheaded)}
    </div>
  );
};

export const FilterPanel: React.FC<FilterPanelProps> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { open, onOpenChange } = props;
  const cm = useCategoryMeta();

  // Why: close dropdown when clicking anywhere outside the FilterPanel wrapper
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  // ONE section per attribute that is BOTH bound by this frontend (TICKET_ATTRIBUTES — a filter the
  // list API has no param for would narrow nothing) and actually declared by the tenant. Three
  // further conditions, each a real state:
  //   - enum only: a scalar or a boolean (the reminder flag) has no bubble row to offer,
  //     and guessing a control for an unknown type is how a filter for a value space nobody declared
  //     appears on screen.
  //   - a German heading: the section is titled by the served labelDe and nothing invents one, so an
  //     attribute declared without it renders no section (its PILLS are unaffected).
  //   - at least one offerable value: an enum whose every value is suppressed has nothing to say
  //     here, while a suppressed value BESIDE an offered one still gets its bubble (see the face
  //     fallback at the bubble itself).
  const attributeSections = props.onAttributeFilterChange
    ? TICKET_ATTRIBUTES.flatMap(binding => {
        // sender_kind is the „Absender" section's kind headings (part 7, 2026-10-11: one section for
        // the one sender pill), never a section of its own.
        if (binding.name === 'sender_kind') return [];
        const attr = cm.attribute(binding.name);
        if (!attr || attr.type !== 'enum' || !attr.labelDe) return [];
        if (!(attr.values ?? []).some(value => cm.attributeFace(attr.name, value) !== null)) return [];
        return [attr];
      })
    : [];

  // The „Absender" organisation section (Task R2) — gated on BOTH halves the way every other
  // optional section here is: the CALLBACK (does this page own the state to filter by) and the
  // DECLARATION (does this tenant's vocabulary even have a sender_kind attribute to group by). A
  // vocabulary with no sender_kind has no kind to label a group with, so there is nothing honest
  // this section could render.
  // part 7, 2026-10-11: EITHER callback opens it — the kind headings are the sender_kind attribute
  // filter, the organisations the registry filter; a page wiring only one still gets that half.
  const senderKindAttr = cm.attribute('sender_kind');
  const showSenderSection = !!(props.onSenderFilterChange || props.onAttributeFilterChange) && !!senderKindAttr;

  // „Warnhinweis" (part 7, 2026-10-11): one bubble per flag the tenant WORDS (servedWord — no raw-key
  // floor: a flag nobody worded is not offered), in the pill's dominance order.
  // THE implication rule in the filter (part 7 fix loop, 2026-10-12): a „Warnhinweis" bubble whose
  // flag a CATEGORY implies (a served implied landing on one of this tenant's primary values) is NOT
  // offered while the category section is — the category bubble stands for it, with the category's
  // own count. The flag is not lost: it still COUNTS in the facet totals (facets.flags is served and
  // untouched here), and its rows stay FILTERABLE through that category bubble, because an implied
  // landing means every row on the category carries the flag. Every other bubble is unchanged. A
  // bubble the clerk has already selected stays offered, so a held selection can always be released.
  const flagImpliedByOfferedPrimary = (id: FlagId): boolean => {
    const by = flagImpliedBy(cm, id);
    return !!props.onCategoryFilterChange && by !== null && cm.categoryKeys.includes(by)
      && !(props.flagFilter ?? []).includes(id);
  };
  const flagOptions = props.onFlagFilterChange
    ? FLAG_DOMINANCE_ORDER.flatMap(id => {
        const word = cm.servedWord('flag', id);
        return word === null || flagImpliedByOfferedPrimary(id) ? [] : [{ id, word }];
      })
    : [];
  // „Formular" (part 7 fix loop, 2026-10-12 — every pill kind is a facet, worded as the pill is):
  // offered only where the tenant SERVES form extraction, the same capability that gates the pill.
  const showFormSection = !!props.onHasFormFilterChange && cm.formExtractionEnabled;

  const hasDropdownSections = !!(
    props.onTimeFilterChange || props.onCategoryFilterChange ||
    props.onReasonCodeFilterChange || props.onFilterUserChange ||
    props.onFilterDateRangeChange || props.onDateRangeChange
  ) || attributeSections.length > 0 || showSenderSection || flagOptions.length > 0 || showFormSection;

  return (
    // `position: relative` makes this wrapper the positioning context the expanded body hangs from
    // (operator ruling 2026-09-16). Only the SEARCH BAR is in the flow here; the sections open as an
    // out-of-flow layer below, so the list underneath keeps its top edge whatever the filter does.
    <div
      ref={wrapperRef}
      data-testid="filter-panel-wrapper"
      style={{ position: 'relative', padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}
    >
      {/* Search bar */}
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.bgPage,
        padding: `0 ${theme.spacing.sm}px`,
      }}>
        <SearchIcon size={16} color={theme.colors.textSecondary} />
        <input
          id="filter-search"
          name="filter-search"
          type="text"
          value={props.search}
          onChange={e => props.onSearchChange(e.target.value)}
          placeholder="Suchen..."
          style={{
            flex: 1, border: 'none', background: 'none', outline: 'none',
            padding: `${theme.spacing.sm}px ${theme.spacing.sm}px`,
            fontSize: 13, fontFamily: theme.font.family, color: theme.colors.navy,
          }}
        />
        {hasDropdownSections && (
          <button
            onClick={() => props.onOpenChange(!props.open)}
            aria-label={props.open ? 'Filter schließen' : 'Filter öffnen'}
            aria-expanded={props.open}
            style={{
              width: 40, height: 40, padding: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (props.open || props.hasActiveFilters) ? 1 : 0.5,
            }}
          >
            <FilterSlidersIcon size={14} active={props.hasActiveFilters} color={props.hasActiveFilters ? theme.colors.navy : theme.colors.textSecondary} />
          </button>
        )}
        {props.hasActiveFilters && (
          <button
            onClick={props.onClearAll}
            aria-label="Alle Filter zurücksetzen"
            style={{
              padding: 4, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, color: theme.colors.textSecondary,
              display: 'flex', alignItems: 'center',
            }}
          >
            &times;
          </button>
        )}
      </div>

      {/* Filter dropdown — an OVERLAY, not a block in the flow (operator ruling 2026-09-16).
          `top: 100%` hangs it from the wrapper's bottom edge, left/right 0 give it the wrapper's
          full width, and the shadow plus the opaque page-white ground make it read as a layer over
          the rows rather than a part of the list.
          It is MOUNTED ONLY WHILE OPEN and fades in, rather than living there permanently at
          opacity 0: an always-mounted panel would put its inputs in the tab order and in the
          screen-reader's path while the clerk believes it is shut. Nothing shifts either way —
          being out of flow, it has no layout box to pre-allocate. */}
      {props.open && hasDropdownSections && (
        <div data-testid="filter-panel-body" style={{
          position: 'absolute',
          left: 0, right: 0, top: '100%',
          zIndex: theme.zIndex.popover,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.md,
          boxShadow: theme.shadows.cardHover,
          border: `1px solid ${theme.colors.borderLight}`,
          animation: 'stx-fadeIn 0.12s ease',
        }}>
          {/* Time bubbles */}
          {props.onTimeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-zeitraum-label" style={filterLabelStyle}>Zeitraum</span>
              <div role="group" aria-labelledby="filter-zeitraum-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TIME_OPTIONS.map(f => {
                  const active = props.timeFilter === f.value;
                  return (
                    <button key={f.value} style={bubbleStyle(active)}
                      onClick={() => props.onTimeFilterChange!(active ? null : f.value)}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category bubbles — role-aware: VNB marktprozess (abbr chip) / gMSB K-codes */}
          {props.onCategoryFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-kategorie-label" style={filterLabelStyle}>{cm.categoryNoun}</span>
              <div role="group" aria-labelledby="filter-kategorie-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {/* categoryKeys (part 7, 2026-10-11 — was performanceCategoryKeys): every served
                    value INCLUDING the unclassified one. The unclassified pill is something a clerk
                    recognises on a row, so it is something they can find here; the performance
                    split stays the stats surfaces' question, not the filter's. */}
                {cm.categoryKeys.map(cat => {
                  const active = props.categoryFilter === cat;
                  return (
                    <button key={cat} style={bubbleStyle(active, cm.isUnclassified(cat) ? cm.swatch(cat) : null)}
                      title={cm.label(cat)}
                      onClick={() => props.onCategoryFilterChange!(active ? null : cat)}>
                      {cm.categoryLabelsFromData ? cm.label(cat) : cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* „Warnhinweis" (part 7, 2026-10-11) — the flag pills as a facet: served words in dominance
              order, each with its facet count (facets.flags, enforced flags only, own dimension
              released). OR within the section, like the secondary axis below. */}
          {flagOptions.length > 0 && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-flag-label" style={filterLabelStyle}>Warnhinweis</span>
              <div role="group" aria-labelledby="filter-flag-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {flagOptions.map(({ id, word }) => {
                  const selected = props.flagFilter ?? [];
                  const active = selected.includes(id);
                  // review round 1: counts only when the server SERVES the dimension. A loaded
                  // facets answer without `flags` (an older core) renders no count span — a settled
                  // „0" there would be a number nobody counted. A served list reads a missing flag
                  // as a real 0, like every other facet.
                  const flagCounts = props.facets?.flags;
                  const count = flagCounts ? (flagCounts.find(v => v.value === id)?.count ?? 0) : undefined;
                  const countUnserved = !!props.facets && flagCounts === undefined;
                  return (
                    <button key={id} type="button" style={bubbleStyle(active)} aria-pressed={active} title={word}
                      onClick={() => props.onFlagFilterChange!(active ? selected.filter(v => v !== id) : [...selected, id])}>
                      <FacetLabel>{word}</FacetLabel>
                      <FacetCount count={count} unsupported={props.facetsUnsupported || countUnserved} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* „Anhang" (part 7 fix loop, 2026-10-12) — the „Formular" pill as a facet: ONE bubble, the
              pill's own word (constants.FORM_WORD), `hasForm=true` on the list, the facets and the
              Excel selection. Its count is the visible bucket's served form-bearing subset; no
              span at all where the list does not serve one (FormFilterCount 'unserved'). */}
          {showFormSection && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-form-label" style={filterLabelStyle}>Anhang</span>
              <div role="group" aria-labelledby="filter-form-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button type="button" style={bubbleStyle(!!props.hasFormFilter)} aria-pressed={!!props.hasFormFilter}
                  onClick={() => props.onHasFormFilterChange!(!props.hasFormFilter)}>
                  <FacetLabel>{FORM_WORD}</FacetLabel>
                  <FacetCount
                    count={typeof props.formFilterCount === 'number' ? props.formFilterCount : undefined}
                    unsupported={props.formFilterCount === undefined || props.formFilterCount === 'unserved'} />
                </button>
              </div>
            </div>
          )}

          {/* Second-axis multi-select — role-aware: VNB anliegen (schema labels) / gMSB reason codes.
              An interactive filter OPTION (selectable, own active state), not a display pill — it
              reuses the secondary-axis resolution accessors (secondaryAxisText/secondaryAxisHover)
              rather than SecondaryAxisPills, which owns markup/click-handling this affordance keeps
              for itself; the FACE still goes through pillFace('axis2', …) so the bubble and the row
              pill never case one code two ways on one screen (walk round 4, 2026-09-08), while the
              title keeps the served casing like every axis2 hover. categoryLabelsFromData still
              decides the ID SET (secondaryAxisKeys floors to [] with no vocabulary/schema, so
              gMSB's floor needs REASON_CODES as its option source) — only the TEXT formula
              collapsed to one, since both accessors resolve any code regardless of source. */}
          {props.onReasonCodeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-grundcode-label" style={filterLabelStyle}>{cm.secondaryAxisNoun}</span>
              <div role="group" aria-labelledby="filter-grundcode-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(cm.categoryLabelsFromData ? cm.secondaryAxisKeys : REASON_CODES.map(rc => rc.code))
                  .map(id => ({ value: id, text: pillFace('axis2', cm.secondaryAxisText(id)), title: cm.secondaryAxisHover(id) }))
                  .map(opt => {
                  const codes = props.reasonCodeFilter ?? [];
                  const active = codes.includes(opt.value);
                  return (
                    <button key={opt.value} style={bubbleStyle(active)}
                      title={opt.title}
                      onClick={() => {
                        const next = active
                          ? codes.filter(c => c !== opt.value)
                          : [...codes, opt.value];
                        props.onReasonCodeFilterChange!(next);
                      }}>
                      {opt.text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attribute sections (wim vocabulary v7, 2026-09-13) — data-driven: which sections exist,
              which values they offer and what each is called all come from the served vocabulary,
              so this block spells no commodity and no sender kind. Same interactive-chip boundary
              the secondary-axis section above draws (own markup + active state, resolution
              accessors only), and placed after it deliberately: a clerk narrows by classification
              first, then by what the mail is about, then by who/when. Each bubble carries a
              FacetCount (Task R2, sender-identity registry) — `undefined` (the loading placeholder)
              while `facets` hasn't arrived yet, else the served count or 0 for a value the facet
              answer is silent on (a settled facets object, zero matches). */}
          {attributeSections.map(attr => {
            const selected = props.attributeFilter?.[attr.name] ?? [];
            const labelId = `filter-attribute-${attr.name}-label`;
            const facetValues = props.facets?.attributes?.[attr.name];
            return (
              <div key={attr.name} style={{ marginBottom: theme.spacing.sm }}>
                <span id={labelId} style={filterLabelStyle}>{attr.labelDe}</span>
                <div role="group" aria-labelledby={labelId} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(attr.values ?? []).map(value => {
                    // THE one place the pill register and the filter register part company: a served
                    // null face means "no pill on a row", which says nothing about whether a clerk
                    // may filter for it — so the bubble falls to the same propercased stand-in an
                    // undeclared value gets (categoryMeta's attributeValueStandIn, shared with the
                    // resolver so there is one propercase in the app). The face itself still comes
                    // from cm.attributeFace: a tenant that words a value keeps its word here too.
                    const face = cm.attributeFace(attr.name, value) ?? attributeValueStandIn(value);
                    const active = selected.includes(value);
                    const count = props.facets
                      ? (facetValues?.find(v => v.value === value)?.count ?? 0)
                      : undefined;
                    return (
                      <button key={value} style={bubbleStyle(active)}
                        title={face}
                        onClick={() => props.onAttributeFilterChange!(
                          attr.name,
                          active ? selected.filter(v => v !== value) : [...selected, value],
                        )}>
                        <FacetLabel>{face}</FacetLabel>
                        <FacetCount count={count} unsupported={props.facetsUnsupported} />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* „Absender" — ONE section for the one sender pill (part 7, 2026-10-11): kind headings
              filter the kind, the organisations under them filter the organisation. See
              SenderOrganisationFilterSection's own header for the grouping rules. */}
          {showSenderSection && (
            <SenderOrganisationFilterSection
              organisations={props.facets?.senderOrganisations ?? []}
              kindOrder={senderKindAttr!.values ?? []}
              faceFor={value => cm.attributeFace('sender_kind', value) ?? attributeValueStandIn(value)}
              kindCounts={props.facets?.attributes?.sender_kind}
              facetsLoaded={!!props.facets}
              selectedKinds={props.attributeFilter?.sender_kind ?? []}
              onKindsChange={props.onAttributeFilterChange
                ? kinds => props.onAttributeFilterChange!('sender_kind', kinds)
                : undefined}
              selected={props.senderFilter ?? []}
              onChange={props.onSenderFilterChange}
              facetsUnsupported={props.facetsUnsupported}
            />
          )}

          {/* Uploader + date range row */}
          {(props.onFilterUserChange || props.onFilterDateRangeChange) && (
            <div style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap' }}>
              {props.onFilterUserChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label htmlFor="filter-uploader" style={filterLabelStyle}>Hochgeladen von</label>
                  <select
                    id="filter-uploader"
                    value={props.filterUser ?? ''}
                    onChange={e => props.onFilterUserChange!(e.target.value)}
                    style={filterInputStyle}
                  >
                    <option value="">Alle</option>
                    {(props.uploaders ?? []).map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Legacy free-text range. Suppressed when the ISO picker is wired. */}
              {props.onFilterDateRangeChange && !props.onDateRangeChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={filterLabelStyle}>{props.dateRangeLabel ?? 'Zeitraum'}</label>
                  <input
                    id="filter-daterange-text"
                    name="filter-daterange-text"
                    type="text"
                    value={props.filterDateRange ?? ''}
                    onChange={e => props.onFilterDateRangeChange!(e.target.value)}
                    placeholder="z.B. 01.01 - 31.01"
                    style={filterInputStyle}
                  />
                </div>
              )}
            </div>
          )}

          {/* ISO date-range picker (ArchivSeite server-side from/to). Own full-width
              block — the native Von/Bis pair needs more room than the free-text field. */}
          {props.onDateRangeChange && (
            <div style={{ marginTop: theme.spacing.sm }}>
              <DateRangeInput
                from={props.from ?? null}
                to={props.to ?? null}
                onChange={props.onDateRangeChange}
                label={props.dateRangeLabel ?? 'Zeitraum'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
